import SourceContextPanel from './SourceContextPanel'
import { useEffect, useMemo, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from 'react'
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  CheckCircle2,
  ChevronRight,
  Copy,
  FileCode2,
  FileText,
  Folder,
  FolderGit2,
  GitBranch,
  GitFork,
  GitPullRequest,
  KeyRound,
  LayoutPanelTop,
  Loader2,
  MessageSquare,
  PlayCircle,
  Plus,
  Send,
  Settings2,
  Shield,
  Sparkles,
  Upload,
  UserCircle2,
  Building2,
  Brain,
  Link2,
  Lock,
  Network,
} from 'lucide-react'
import api, {
  type ForgeKnowledgeEdge,
  type ForgeKnowledgeNode,
  type WorkspaceAccountContext,
  type WorkspaceAccountInvitation,
  type WorkspaceBranchContextBundle,
  type WorkspaceCodeIntelligenceRecord,
  type WorkspaceCommitIntent,
  type WorkspaceContextfulAccess,
  type WorkspaceGitHttpCredential,
  type WorkspaceImportPlan,
  type WorkspaceOrganizationSettings,
  type WorkspaceRepoMember,
  type WorkspacePersonalSettings,
  type WorkspacePullRequest,
  type WorkspacePullRequestEvidence,
  type WorkspaceRepoDetail,
  type WorkspaceRepoSettings,
  type WorkspaceRepoSummary,
  type WorkspaceSshKey,
} from './services/api'

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

async function copyText(value: string) {
  await navigator.clipboard.writeText(value)
}

function deriveRepoNameFromArchive(file: File) {
  return file.name.replace(/\.zip$/i, '').replace(/[-_]+/g, ' ').trim()
}

function formatVisibilityLabel(value: string) {
  const normalized = String(value || 'private').replace(/^Visibility\./, '')
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

function commitAuthor(commit: Record<string, unknown>) {
  return String(commit.author_name || commit.author || commit.author_email || 'Unknown author')
}

function commitSha(commit: Record<string, unknown> | undefined) {
  return String(commit?.sha || commit?.hash || commit?.id || '')
}

function codeNavigationFacts(codeIntelligence: WorkspaceCodeIntelligenceRecord | null) {
  return (codeIntelligence?.model.facts_preview || [])
    .filter((fact) => ['class', 'function', 'method', 'symbol', 'module', 'config_reference', 'build_system'].includes(fact.fact_type))
    .slice(0, 8)
}

const fieldClass = 'w-full rounded-md border border-[var(--forge-border)] bg-[var(--forge-panel)] px-3 py-2 text-sm text-[var(--forge-text)] outline-none transition focus:border-[var(--forge-accent)] focus:ring-2 focus:ring-[var(--forge-accent-soft)]'
const textareaClass = `${fieldClass} min-h-24`
const buttonPrimary = 'inline-flex items-center justify-center gap-2 rounded-md border border-[var(--forge-accent)] bg-[var(--forge-accent)] px-4 py-2 text-sm font-medium text-white transition hover:bg-[var(--forge-accent-strong)] disabled:cursor-not-allowed disabled:opacity-60'
const buttonSecondary = 'inline-flex items-center justify-center gap-2 rounded-md border border-[var(--forge-border)] bg-[var(--forge-panel)] px-4 py-2 text-sm font-medium text-[var(--forge-text)] transition hover:border-[var(--forge-border-strong)] hover:bg-[var(--forge-panel-subtle)] disabled:cursor-not-allowed disabled:opacity-60'
const cardClass = 'rounded-md border border-[var(--forge-border)] bg-[var(--forge-panel)]'
const subtleCardClass = 'rounded-md border border-[var(--forge-border)] bg-[var(--forge-panel-muted)]'

type WorkspaceMode = 'organization-home' | 'organization-settings' | 'personal-settings' | 'repository'

function workspaceMode(pathname: string, repoId?: string) {
  if (pathname.startsWith('/settings/organization')) return 'organization-settings' as WorkspaceMode
  if (pathname.startsWith('/settings/personal')) return 'personal-settings' as WorkspaceMode
  if (repoId) return 'repository' as WorkspaceMode
  return 'organization-home' as WorkspaceMode
}

function repoTabFromPath(pathname: string) {
  const parts = pathname.split('/').filter(Boolean)
  const segment = parts[parts.length - 1]
  if (!segment || segment === 'repositories') return 'overview'
  if (['overview', 'code', 'branches', 'pulls', 'architect', 'source-context', 'access', 'settings'].includes(segment)) return segment
  return 'overview'
}

export default function ForgeWorkspaceView() {
  const { repoId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const mode = workspaceMode(location.pathname, repoId)
  const activeTab = repoTabFromPath(location.pathname)

  const [repos, setRepos] = useState<WorkspaceRepoSummary[]>([])
  const [repoDetail, setRepoDetail] = useState<WorkspaceRepoDetail | null>(null)
  const [codeIntelligence, setCodeIntelligence] = useState<WorkspaceCodeIntelligenceRecord | null>(null)
  const [repoSettings, setRepoSettings] = useState<WorkspaceRepoSettings | null>(null)
  const [repoMembers, setRepoMembers] = useState<WorkspaceRepoMember[]>([])
  const [knowledgeNodes, setKnowledgeNodes] = useState<ForgeKnowledgeNode[]>([])
  const [knowledgeEdges, setKnowledgeEdges] = useState<ForgeKnowledgeEdge[]>([])
  const [personalSettings, setPersonalSettings] = useState<WorkspacePersonalSettings | null>(null)
  const [organizationSettings, setOrganizationSettings] = useState<WorkspaceOrganizationSettings | null>(null)
  const [accountContext, setAccountContext] = useState<WorkspaceAccountContext | null>(null)
  const [contextfulAccess, setContextfulAccess] = useState<WorkspaceContextfulAccess | null>(null)
  const [treePath, setTreePath] = useState('')
  const [treeRef, setTreeRef] = useState('HEAD')
  const [treeEntries, setTreeEntries] = useState<Array<{ name: string; path: string; type: 'blob' | 'tree'; size?: number }>>([])
  const [fileContent, setFileContent] = useState('')
  const [selectedFile, setSelectedFile] = useState('')
  const [commits, setCommits] = useState<Array<Record<string, unknown>>>([])
  const [selectedPrId, setSelectedPrId] = useState<string | null>(null)
  const [prDiff, setPrDiff] = useState('')
  const [importPlan, setImportPlan] = useState<WorkspaceImportPlan | null>(null)
  const [prEvidence, setPrEvidence] = useState<WorkspacePullRequestEvidence | null>(null)
  const [commitIntent, setCommitIntent] = useState<WorkspaceCommitIntent | null>(null)
  const [contextBundle, setContextBundle] = useState<WorkspaceBranchContextBundle | null>(null)
  const [sshKeys, setSshKeys] = useState<WorkspaceSshKey[]>([])
  const [authorizedKeys, setAuthorizedKeys] = useState('')
  const [gitHttpCredentials, setGitHttpCredentials] = useState<WorkspaceGitHttpCredential | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const uploadRef = useRef<HTMLInputElement>(null)

  const [createForm, setCreateForm] = useState({ name: '', description: '', visibility: 'private' })
  const [forkForm, setForkForm] = useState({ source_url: '', name: '', description: '', visibility: 'private' })
  const [branchForm, setBranchForm] = useState({ branch_name: '', from_ref: 'main' })
  const [prForm, setPrForm] = useState({ title: '', description: '', source_branch: '', target_branch: 'main' })
  const [sshForm, setSshForm] = useState({ title: '', public_key: '' })
  const [memberForm, setMemberForm] = useState({ email: '', display_name: '', role: 'writer' as WorkspaceRepoMember['role'] })
  const [knowledgeLinkForm, setKnowledgeLinkForm] = useState({ source_node_id: '', target_node_id: '', edge_type: 'relates_to', context: '' })
  const [personalForm, setPersonalForm] = useState({ preferred_clone_protocol: 'https', default_landing_page: 'repositories' })
  const [organizationForm, setOrganizationForm] = useState({ org_display_name: '', allow_signup: true, default_repo_visibility: 'private', require_pull_request_reviews: true, allow_public_repositories: false, allow_remote_imports: true, blocked_import_hosts: '', import_retention_mode: 'reference-only' })
  const [accountOrgForm, setAccountOrgForm] = useState({ name: '', description: '' })
  const [accountTeamForm, setAccountTeamForm] = useState({ organization_id: '', name: '', description: '' })
  const [accountInviteForm, setAccountInviteForm] = useState({ email: '', role: 'member', organization_id: '', team_id: '' })
  const [repoSettingsForm, setRepoSettingsForm] = useState({ description: '', visibility: 'private', default_branch: 'main', homepage_url: '', documentation_path: 'README.md', branch_protection_enabled: false, require_pull_request_reviews: true })

  const selectedPullRequest = useMemo(
    () => (repoDetail?.pull_requests || []).find((pr) => pr.pr_id === selectedPrId) || repoDetail?.pull_requests?.[0] || null,
    [repoDetail?.pull_requests, selectedPrId],
  )
  const knowledgeProjectId = codeIntelligence?.project_id || codeIntelligence?.model.project_id || ''

  async function loadRepos() {
    setLoading(true)
    setError(null)
    try {
      const data = await api.listWorkspaceRepos()
      setRepos(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repositories')
    } finally {
      setLoading(false)
    }
  }

  async function loadPersonalSettings() {
    try {
      const payload = await api.getWorkspacePersonalSettings()
      setPersonalSettings(payload)
      setPersonalForm({
        preferred_clone_protocol: payload.preferred_clone_protocol,
        default_landing_page: payload.default_landing_page,
      })
    } catch {
      setPersonalSettings(null)
    }
  }

  async function loadOrganizationSettings() {
    try {
      const payload = await api.getWorkspaceOrganizationSettings()
      setOrganizationSettings(payload)
      setOrganizationForm({
        org_display_name: payload.org_display_name,
        allow_signup: payload.allow_signup,
        default_repo_visibility: payload.default_repo_visibility,
        require_pull_request_reviews: payload.require_pull_request_reviews,
        allow_public_repositories: payload.allow_public_repositories,
        allow_remote_imports: payload.allow_remote_imports,
        blocked_import_hosts: (payload.blocked_import_hosts || []).join(', '),
        import_retention_mode: payload.import_retention_mode || 'reference-only',
      })
    } catch {
      setOrganizationSettings(null)
    }
  }

  async function loadAccountContext() {
    try {
      const payload = await api.getWorkspaceAccountContext()
      setAccountContext(payload)
      const firstOrg = payload.organizations[0]?.organization_id || ''
      setAccountTeamForm((current) => ({ ...current, organization_id: current.organization_id || firstOrg }))
      setAccountInviteForm((current) => ({ ...current, organization_id: current.organization_id || firstOrg }))
    } catch {
      setAccountContext(null)
    }
  }

  async function loadContextfulAccess() {
    try {
      const payload = await api.getWorkspaceContextfulAccess()
      setContextfulAccess(payload)
    } catch {
      setContextfulAccess(null)
    }
  }

  async function loadKnowledgeGraph(projectId: string) {
    try {
      const [nodesPayload, edgesPayload] = await Promise.all([
        api.listKnowledgeNodes(projectId, undefined, undefined, 100),
        api.listKnowledgeEdges(projectId),
      ])
      setKnowledgeNodes(nodesPayload.nodes || [])
      setKnowledgeEdges(edgesPayload.edges || [])
      setKnowledgeLinkForm((current) => {
        const sourceStillExists = nodesPayload.nodes.some((node) => node.node_id === current.source_node_id)
        const targetStillExists = nodesPayload.nodes.some((node) => node.node_id === current.target_node_id)
        const source = sourceStillExists ? current.source_node_id : nodesPayload.nodes[0]?.node_id || ''
        const targetCandidate = targetStillExists ? current.target_node_id : nodesPayload.nodes.find((node) => node.node_id !== source)?.node_id || ''
        return { ...current, source_node_id: source, target_node_id: targetCandidate }
      })
    } catch {
      setKnowledgeNodes([])
      setKnowledgeEdges([])
    }
  }

  async function loadRepoDetail(currentRepoId: string, nextRef?: string) {
    setBusy('repo-detail')
    setError(null)
    try {
      const detail = await api.getWorkspaceRepo(currentRepoId)
      const effectiveRef = nextRef || detail.default_branch || 'HEAD'
      const [treeResult, commitResult, prResult, keysExportResult, keyDataResult, settingsResult, membersResult, httpCredentialsResult, codeIntelligenceResult] = await Promise.allSettled([
        api.listWorkspaceTree(currentRepoId, effectiveRef, ''),
        api.listWorkspaceCommits(currentRepoId, 20),
        api.listWorkspacePullRequests(currentRepoId),
        api.exportWorkspaceAuthorizedKeys(),
        api.listWorkspaceSshKeys(),
        api.getWorkspaceRepoSettings(currentRepoId),
        api.listWorkspaceRepoMembers(currentRepoId),
        api.getWorkspaceGitHttpCredentials(),
        api.getWorkspaceCodeIntelligence(currentRepoId),
      ])
      const prs = prResult.status === 'fulfilled' ? prResult.value : []
      const tree = treeResult.status === 'fulfilled' ? treeResult.value : { entries: [] }
      const commitData = commitResult.status === 'fulfilled' ? commitResult.value : { commits: [], count: 0 }
      const keysExport = keysExportResult.status === 'fulfilled' ? keysExportResult.value : { authorized_keys: '', count: 0 }
      const keyData = keyDataResult.status === 'fulfilled' ? keyDataResult.value : []
      const settings = settingsResult.status === 'fulfilled' ? settingsResult.value : null
      const members = membersResult.status === 'fulfilled' ? membersResult.value : []
      const httpCredentials = httpCredentialsResult.status === 'fulfilled' ? httpCredentialsResult.value : null
      const codeIntel = codeIntelligenceResult.status === 'fulfilled' ? codeIntelligenceResult.value : null

      setRepoDetail({ ...detail, pull_requests: prs })
      setCodeIntelligence(codeIntel)
      setRepoSettings(settings)
      setRepoMembers(members)
      if (settings) {
        setRepoSettingsForm({
          description: settings.description,
          visibility: settings.visibility,
          default_branch: settings.default_branch,
          homepage_url: settings.homepage_url,
          documentation_path: settings.documentation_path,
          branch_protection_enabled: settings.branch_protection_enabled,
          require_pull_request_reviews: settings.require_pull_request_reviews,
        })
      }
      setTreeRef(effectiveRef)
      setTreePath('')
      setTreeEntries(tree.entries)
      setCommits(commitData.commits || [])
      const latestSha = commitSha((commitData.commits || [])[0])
      if (latestSha) {
        try {
          setCommitIntent(await api.getWorkspaceCommitIntent(currentRepoId, latestSha))
        } catch {
          setCommitIntent(null)
        }
      } else {
        setCommitIntent(null)
      }
      setAuthorizedKeys(keysExport.authorized_keys)
      setSshKeys(keyData)
      setGitHttpCredentials(httpCredentials)
      setSelectedPrId((current) => (prs.some((pr) => pr.pr_id === current) ? current : prs[0]?.pr_id || null))
      setBranchForm((current) => ({ ...current, from_ref: detail.default_branch || 'main' }))
      setPrForm((current) => ({
        ...current,
        source_branch: current.source_branch || detail.branches.find((branch) => branch !== detail.default_branch) || detail.branches[0] || '',
        target_branch: detail.default_branch || 'main',
      }))

      const preferredPath = settings?.documentation_path || 'README.md'
      const preferredPreview = tree.entries.find((entry) => entry.path.toLowerCase() === preferredPath.toLowerCase())
        || tree.entries.find((entry) => entry.path.toLowerCase() === 'docs/overview.md')
        || tree.entries.find((entry) => entry.path.toLowerCase() === 'readme.md')
        || tree.entries.find((entry) => entry.type === 'blob')
      if (preferredPreview) {
        try {
          const file = await api.readWorkspaceFile(currentRepoId, preferredPreview.path, effectiveRef)
          setSelectedFile(preferredPreview.path)
          setFileContent(file.content)
        } catch {
          setSelectedFile('')
          setFileContent('')
        }
      } else {
        setSelectedFile('')
        setFileContent('')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repository details')
    } finally {
      setBusy(null)
    }
  }

  async function loadPrDiff(currentRepoId: string, pr: WorkspacePullRequest) {
    setBusy(`diff-${pr.pr_id}`)
    try {
      const [diffPayload, evidencePayload] = await Promise.all([
        api.diffWorkspaceRepo(currentRepoId, pr.target_branch, pr.source_branch),
        api.getWorkspacePullRequestEvidence(currentRepoId, pr.pr_id),
      ])
      setPrDiff(diffPayload.diff)
      setPrEvidence(evidencePayload)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pull request diff')
    } finally {
      setBusy(null)
    }
  }

  async function loadTree(currentRepoId: string, ref: string, path: string) {
    setBusy('tree')
    try {
      const tree = await api.listWorkspaceTree(currentRepoId, ref, path)
      setTreeEntries(tree.entries)
      setTreePath(path)
      setSelectedFile('')
      setFileContent('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repository tree')
    } finally {
      setBusy(null)
    }
  }

  async function openFile(currentRepoId: string, path: string) {
    setBusy('file')
    try {
      const file = await api.readWorkspaceFile(currentRepoId, path, treeRef)
      setSelectedFile(path)
      setFileContent(file.content)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load file'
      if (message.includes('Repository file not found')) {
        setSelectedFile(path)
        setFileContent('This file could not be loaded from the current branch.')
      } else {
        setError(message)
      }
    } finally {
      setBusy(null)
    }
  }

  useEffect(() => {
    void loadRepos()
    void loadPersonalSettings()
    void loadOrganizationSettings()
    void loadAccountContext()
    void loadContextfulAccess()
  }, [])

  useEffect(() => {
    if (repoId || location.pathname !== '/repositories' || repos.length === 0) return
    const params = new URLSearchParams(location.search)
    const requestedRepo = params.get('repo')
    if (!requestedRepo) return
    const normalized = requestedRepo.toLowerCase()
    const match = repos.find((repo) =>
      [repo.repo_id, repo.name, repo.git_repo_name, repo.canonical_path]
        .some((value) => value.toLowerCase() === normalized || value.toLowerCase().includes(normalized)),
    )
    if (match) {
      navigate(`/repositories/${match.repo_id}/architect`, { replace: true })
    }
  }, [location.pathname, location.search, navigate, repoId, repos])

  useEffect(() => {
    if (repoId) {
      void loadRepoDetail(repoId)
    } else {
      setRepoDetail(null)
      setRepoSettings(null)
      setRepoMembers([])
      setSelectedPrId(null)
      setPrDiff('')
      setPrEvidence(null)
      setCommitIntent(null)
      setContextBundle(null)
      setCodeIntelligence(null)
      setKnowledgeNodes([])
      setKnowledgeEdges([])
    }
  }, [repoId])

  useEffect(() => {
    if (repoId) {
      void loadRepoDetail(repoId, treeRef)
    }
  }, [repoId, activeTab])

  useEffect(() => {
    if (repoId && selectedPullRequest) {
      void loadPrDiff(repoId, selectedPullRequest)
    } else {
      setPrDiff('')
      setPrEvidence(null)
    }
  }, [repoId, selectedPullRequest?.pr_id])

  useEffect(() => {
    if (activeTab === 'architect' && knowledgeProjectId) {
      void loadKnowledgeGraph(knowledgeProjectId)
    }
  }, [activeTab, knowledgeProjectId])

  async function handleCreateRepo() {
    if (!createForm.name.trim()) return
    setBusy('create-repo')
    try {
      const created = await api.createWorkspaceRepo(createForm)
      setCreateForm({ name: '', description: '', visibility: 'private' })
      await loadRepos()
      navigate(`/repositories/${created.repo_id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create repository')
    } finally {
      setBusy(null)
    }
  }

  async function handlePreviewImportPlan() {
    if (!forkForm.source_url.trim()) return
    setBusy('import-plan')
    try {
      const plan = await api.previewWorkspaceRemoteImport({ ...forkForm, inspect_remote: true })
      setImportPlan(plan)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview remote import')
    } finally {
      setBusy(null)
    }
  }

  async function handleForkRepo() {
    if (!forkForm.name.trim() || !forkForm.source_url.trim()) return
    setBusy('fork-repo')
    try {
      const result = await api.remoteImportWorkspaceRepo({ ...forkForm, inspect_remote: true })
      setForkForm({ source_url: '', name: '', description: '', visibility: 'private' })
      setImportPlan(result.import_plan)
      await loadRepos()
      navigate(`/repositories/${result.repo.repo_id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import remote repository')
    } finally {
      setBusy(null)
    }
  }

  async function handleImportRepo(file: File | null) {
    if (!file) return
    setBusy('import-repo')
    try {
      const created = await api.importWorkspaceRepo({
        name: createForm.name.trim() || deriveRepoNameFromArchive(file),
        description: createForm.description,
        visibility: createForm.visibility,
        archive: file,
      })
      setCreateForm({ name: '', description: '', visibility: 'private' })
      if (uploadRef.current) uploadRef.current.value = ''
      await loadRepos()
      navigate(`/repositories/${created.repo_id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import repository')
    } finally {
      setBusy(null)
    }
  }

  async function handleCreateBranch() {
    if (!repoId || !branchForm.branch_name.trim()) return
    setBusy('create-branch')
    try {
      const createdBranch = branchForm.branch_name.trim()
      await api.createWorkspaceBranch(repoId, branchForm)
      setBranchForm({ branch_name: '', from_ref: repoDetail?.default_branch || 'main' })
      await loadRepoDetail(repoId, createdBranch)
      navigate(`/repositories/${repoId}/branches`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create branch')
    } finally {
      setBusy(null)
    }
  }

  async function handleCreateContextBundle(branch: string) {
    if (!repoId || !branch.trim()) return
    setBusy(`context-bundle-${branch}`)
    try {
      const bundle = await api.createWorkspaceBranchContextBundle(repoId, branch, {
        purpose: 'agent-runtime',
        task: `Review ${branch} before merge`,
        allowed_tools: ['git_read', 'test_read'],
      })
      setContextBundle(bundle)
      setNotice(`Context bundle ready for ${bundle.branch}.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create context bundle')
    } finally {
      setBusy(null)
    }
  }

  async function handleCreatePr() {
    if (!repoId || !prForm.title.trim() || !prForm.source_branch.trim() || !prForm.target_branch.trim()) return
    setBusy('create-pr')
    try {
      const created = await api.createWorkspacePullRequest(repoId, prForm)
      setPrForm((current) => ({ ...current, title: '', description: '' }))
      await loadRepoDetail(repoId)
      setSelectedPrId(created.pr_id)
      navigate(`/repositories/${repoId}/pulls`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create pull request')
    } finally {
      setBusy(null)
    }
  }

  async function handleMergePr(pr: WorkspacePullRequest) {
    if (!repoId) return
    setBusy(`merge-${pr.pr_id}`)
    try {
      const merged = await api.mergeWorkspacePullRequest(repoId, pr.pr_id)
      setRepoDetail((current) => {
        if (!current) return current
        return {
          ...current,
          pull_requests: current.pull_requests.map((entry) => (entry.pr_id === merged.pr_id ? merged : entry)),
        }
      })
      await loadRepoDetail(repoId)
      setSelectedPrId(merged.pr_id)
      setNotice(`Pull request merged. Merge commit: ${merged.merge_commit || 'recorded'}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge pull request')
    } finally {
      setBusy(null)
    }
  }

  async function handleAddSshKey() {
    if (!sshForm.title.trim() || !sshForm.public_key.trim()) return
    setBusy('ssh-add')
    try {
      await api.createWorkspaceSshKey(sshForm)
      setSshForm({ title: '', public_key: '' })
      const [keys, exported, personal] = await Promise.all([api.listWorkspaceSshKeys(), api.exportWorkspaceAuthorizedKeys(), api.getWorkspacePersonalSettings()])
      setSshKeys(keys)
      setAuthorizedKeys(exported.authorized_keys)
      setPersonalSettings(personal)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add SSH key')
    } finally {
      setBusy(null)
    }
  }

  async function handleDeleteSshKey(keyId: string) {
    setBusy(`ssh-delete-${keyId}`)
    try {
      await api.deleteWorkspaceSshKey(keyId)
      const [keys, exported, personal] = await Promise.all([api.listWorkspaceSshKeys(), api.exportWorkspaceAuthorizedKeys(), api.getWorkspacePersonalSettings()])
      setSshKeys(keys)
      setAuthorizedKeys(exported.authorized_keys)
      setPersonalSettings(personal)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete SSH key')
    } finally {
      setBusy(null)
    }
  }

  async function handleRotateGitHttpCredentials() {
    setBusy('rotate-http-credentials')
    try {
      const next = await api.rotateWorkspaceGitHttpCredentials()
      setGitHttpCredentials(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate Git credentials')
    } finally {
      setBusy(null)
    }
  }

  async function handleSaveRepoMember() {
    if (!repoId || !memberForm.email.trim()) return
    setBusy('save-repo-member')
    try {
      const members = await api.upsertWorkspaceRepoMember(repoId, memberForm)
      setRepoMembers(members)
      setMemberForm({ email: '', display_name: '', role: 'writer' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save repository member')
    } finally {
      setBusy(null)
    }
  }

  async function handleDeleteRepoMember(email: string) {
    if (!repoId) return
    setBusy(`delete-repo-member-${email}`)
    try {
      await api.deleteWorkspaceRepoMember(repoId, email)
      const members = await api.listWorkspaceRepoMembers(repoId)
      setRepoMembers(members)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete repository member')
    } finally {
      setBusy(null)
    }
  }

  async function handleSavePersonalSettings() {
    setBusy('save-personal-settings')
    try {
      const saved = await api.updateWorkspacePersonalSettings(personalForm)
      setPersonalSettings(saved)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save personal settings')
    } finally {
      setBusy(null)
    }
  }

  async function handleSaveOrganizationSettings() {
    setBusy('save-org-settings')
    try {
      const saved = await api.updateWorkspaceOrganizationSettings({
        ...organizationForm,
        blocked_import_hosts: organizationForm.blocked_import_hosts.split(',').map((item) => item.trim()).filter(Boolean),
      })
      setOrganizationSettings(saved)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save organization settings')
    } finally {
      setBusy(null)
    }
  }

  async function handleCreateAccountOrganization() {
    if (!accountOrgForm.name.trim()) return
    setBusy('create-account-organization')
    try {
      const saved = await api.createWorkspaceAccountOrganization(accountOrgForm)
      setAccountContext(saved)
      setAccountOrgForm({ name: '', description: '' })
      setNotice('Organization created.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization')
    } finally {
      setBusy(null)
    }
  }

  async function handleCreateAccountTeam() {
    if (!accountTeamForm.organization_id || !accountTeamForm.name.trim()) return
    setBusy('create-account-team')
    try {
      const saved = await api.createWorkspaceAccountTeam(accountTeamForm)
      setAccountContext(saved)
      setAccountTeamForm((current) => ({ ...current, name: '', description: '' }))
      setNotice('Team created.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team')
    } finally {
      setBusy(null)
    }
  }

  async function handleCreateAccountInvitation() {
    if (!accountInviteForm.email.trim()) return
    setBusy('create-account-invitation')
    try {
      const saved = await api.createWorkspaceAccountInvitation({
        email: accountInviteForm.email,
        role: accountInviteForm.role,
        organization_id: accountInviteForm.organization_id || undefined,
        team_id: accountInviteForm.team_id || undefined,
      })
      setAccountContext(saved)
      setAccountInviteForm((current) => ({ ...current, email: '' }))
      setNotice('Invitation created.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invitation')
    } finally {
      setBusy(null)
    }
  }

  async function handleSaveRepoSettings() {
    if (!repoId) return
    setBusy('save-repo-settings')
    try {
      const saved = await api.updateWorkspaceRepoSettings(repoId, repoSettingsForm)
      setRepoSettings(saved)
      await loadRepoDetail(repoId, repoSettingsForm.default_branch)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save repository settings')
    } finally {
      setBusy(null)
    }
  }

  async function handleUpsertFile(path: string, content: string, commitMessage: string) {
    if (!repoId) return
    setBusy('save-file')
    try {
      const branch = treeRef || repoDetail?.default_branch || 'main'
      const result = await api.upsertWorkspaceFile(repoId, {
        path,
        content,
        ref: branch,
        commit_message: commitMessage,
      })
      await loadRepoDetail(repoId, result.branch)
      await loadTree(repoId, result.branch, treePath)
      await openFile(repoId, result.path)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save repository file')
    } finally {
      setBusy(null)
    }
  }

  async function handleDeleteFile(path: string, commitMessage: string) {
    if (!repoId) return
    setBusy('delete-file')
    try {
      const branch = treeRef || repoDetail?.default_branch || 'main'
      await api.deleteWorkspaceFile(repoId, {
        path,
        ref: branch,
        commit_message: commitMessage,
      })
      setSelectedFile('')
      setFileContent('')
      await loadRepoDetail(repoId, branch)
      await loadTree(repoId, branch, treePath)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete repository file')
    } finally {
      setBusy(null)
    }
  }

  async function handleCreateKnowledgeLink() {
    if (!knowledgeProjectId || !knowledgeLinkForm.source_node_id || !knowledgeLinkForm.target_node_id) return
    setBusy('knowledge-link')
    try {
      await api.linkKnowledgeNodes(
        knowledgeProjectId,
        knowledgeLinkForm.source_node_id,
        knowledgeLinkForm.target_node_id,
        knowledgeLinkForm.edge_type,
        0.8,
        {
          context: knowledgeLinkForm.context.trim(),
          source: 'forge-architect',
          repo_id: repoId || '',
        },
      )
      setKnowledgeLinkForm((current) => ({ ...current, context: '' }))
      await loadKnowledgeGraph(knowledgeProjectId)
      setNotice('Technical knowledge link created.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create knowledge link')
    } finally {
      setBusy(null)
    }
  }

  async function handleActivateContextfulAccess() {
    setBusy('contextful-signup')
    try {
      const payload = await api.signupWorkspaceContextfulAccess()
      setContextfulAccess(payload)
      setNotice('Contextful access activated for this Forge user.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate Contextful access')
    } finally {
      setBusy(null)
    }
  }

  async function handleRecordContextfulUsage() {
    const payload = await api.recordWorkspaceContextfulUsage({ repo_id: repoId || '', action: 'architect-chat' })
    setContextfulAccess(payload)
    return payload
  }

  if (loading && mode === 'organization-home') {
    return <div className="flex min-h-[40vh] items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
  }

  return (
    <div className="space-y-6">
      {error ? <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
      {notice ? <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{notice}</div> : null}

      {mode === 'organization-home' ? (
        <OrganizationHome
          repos={repos}
          loading={loading}
          busy={busy}
          createForm={createForm}
          setCreateForm={setCreateForm}
          forkForm={forkForm}
          setForkForm={setForkForm}
          importPlan={importPlan}
          uploadRef={uploadRef}
          organizationSettings={organizationSettings}
          onCreateRepo={handleCreateRepo}
          onPreviewImportPlan={handlePreviewImportPlan}
          onForkRepo={handleForkRepo}
          onImportRepo={handleImportRepo}
        />
      ) : null}

      {mode === 'organization-settings' ? (
        <OrganizationSettingsPage
          settings={organizationSettings}
          form={organizationForm}
          setForm={setOrganizationForm}
          accountContext={accountContext}
          accountOrgForm={accountOrgForm}
          setAccountOrgForm={setAccountOrgForm}
          accountTeamForm={accountTeamForm}
          setAccountTeamForm={setAccountTeamForm}
          accountInviteForm={accountInviteForm}
          setAccountInviteForm={setAccountInviteForm}
          busy={busy}
          onSave={handleSaveOrganizationSettings}
          onCreateOrganization={handleCreateAccountOrganization}
          onCreateTeam={handleCreateAccountTeam}
          onCreateInvitation={handleCreateAccountInvitation}
        />
      ) : null}

      {mode === 'personal-settings' ? (
        <PersonalSettingsPage
          settings={personalSettings}
          form={personalForm}
          setForm={setPersonalForm}
          sshKeys={sshKeys}
          busy={busy}
          onSave={handleSavePersonalSettings}
        />
      ) : null}

      {mode === 'repository' ? (
        <RepositoryDetailPage
          repoId={repoId!}
          repoDetail={repoDetail}
          repoSettings={repoSettings}
          activeTab={activeTab}
          busy={busy}
          treeRef={treeRef}
          setTreeRef={setTreeRef}
          treePath={treePath}
          treeEntries={treeEntries}
          fileContent={fileContent}
          selectedFile={selectedFile}
          commits={commits}
          commitIntent={commitIntent}
          selectedPrId={selectedPrId}
          setSelectedPrId={setSelectedPrId}
          selectedPullRequest={selectedPullRequest}
          prDiff={prDiff}
          prEvidence={prEvidence}
          codeIntelligence={codeIntelligence}
          contextfulAccess={contextfulAccess}
          knowledgeProjectId={knowledgeProjectId}
          knowledgeNodes={knowledgeNodes}
          knowledgeEdges={knowledgeEdges}
          knowledgeLinkForm={knowledgeLinkForm}
          setKnowledgeLinkForm={setKnowledgeLinkForm}
          contextBundle={contextBundle}
          branchForm={branchForm}
          setBranchForm={setBranchForm}
          prForm={prForm}
          setPrForm={setPrForm}
          sshForm={sshForm}
          setSshForm={setSshForm}
          sshKeys={sshKeys}
          authorizedKeys={authorizedKeys}
          gitHttpCredentials={gitHttpCredentials}
          repoMembers={repoMembers}
          memberForm={memberForm}
          setMemberForm={setMemberForm}
          repoSettingsForm={repoSettingsForm}
          setRepoSettingsForm={setRepoSettingsForm}
          onLoadTree={loadTree}
          onOpenFile={openFile}
          onUpsertFile={handleUpsertFile}
          onDeleteFile={handleDeleteFile}
          onCreateBranch={handleCreateBranch}
          onCreateContextBundle={handleCreateContextBundle}
          onCreatePr={handleCreatePr}
          onMergePr={handleMergePr}
          onActivateContextfulAccess={handleActivateContextfulAccess}
          onRecordContextfulUsage={handleRecordContextfulUsage}
          onCreateKnowledgeLink={handleCreateKnowledgeLink}
          onAddSshKey={handleAddSshKey}
          onDeleteSshKey={handleDeleteSshKey}
          onRotateGitHttpCredentials={handleRotateGitHttpCredentials}
          onSaveRepoMember={handleSaveRepoMember}
          onDeleteRepoMember={handleDeleteRepoMember}
          onSaveRepoSettings={handleSaveRepoSettings}
        />
      ) : null}
    </div>
  )
}

function OrganizationHome({ repos, loading, busy, createForm, setCreateForm, forkForm, setForkForm, importPlan, uploadRef, organizationSettings, onCreateRepo, onPreviewImportPlan, onForkRepo, onImportRepo }: {
  repos: WorkspaceRepoSummary[]
  loading: boolean
  busy: string | null
  createForm: { name: string; description: string; visibility: string }
  setCreateForm: Dispatch<SetStateAction<{ name: string; description: string; visibility: string }>>
  forkForm: { source_url: string; name: string; description: string; visibility: string }
  setForkForm: Dispatch<SetStateAction<{ source_url: string; name: string; description: string; visibility: string }>>
  importPlan: WorkspaceImportPlan | null
  uploadRef: RefObject<HTMLInputElement | null>
  organizationSettings: WorkspaceOrganizationSettings | null
  onCreateRepo: () => Promise<void>
  onPreviewImportPlan: () => Promise<void>
  onForkRepo: () => Promise<void>
  onImportRepo: (file: File | null) => Promise<void>
}) {
  const sortedRepos = [...repos].sort((left, right) => left.name.localeCompare(right.name))
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showForkForm, setShowForkForm] = useState(false)
  const [repoFilter, setRepoFilter] = useState('')
  const filteredRepos = sortedRepos.filter((repo) => `${repo.name} ${repo.description || ''}`.toLowerCase().includes(repoFilter.toLowerCase().trim()))

  return (
    <div className="space-y-4">
      <section className="space-y-4">
        <div className="bg-transparent">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-950">{organizationSettings?.org_display_name || 'Your repositories'}</h1>
              <div className="mt-1 text-sm text-slate-500">Private by default, with deliberate sharing for safe collaboration.</div>
            </div>
            <div className="flex flex-1 flex-wrap items-center justify-end gap-3 xl:max-w-3xl">
              <input
                aria-label="Search repositories"
                value={repoFilter}
                onChange={(e) => setRepoFilter(e.target.value)}
                placeholder="Find a repository…"
                className="min-w-[16rem] flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              />
              <button onClick={() => { setShowCreateForm((current) => !current); setShowForkForm(false) }} className={buttonPrimary}><Plus className="h-4 w-4" /> New</button>
              <button onClick={() => uploadRef.current?.click()} disabled={busy === 'import-repo'} className={buttonSecondary}>{busy === 'import-repo' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import</button>
              <button onClick={() => { setShowForkForm((current) => !current); setShowCreateForm(false) }} className={buttonSecondary}><GitFork className="h-4 w-4" /> Fork</button>
              <Link to="/settings/organization" className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">Settings</Link>
              <input ref={uploadRef} type="file" accept=".zip" className="hidden" onChange={(e) => void onImportRepo(e.target.files?.[0] || null)} />
            </div>
          </div>
        </div>
        {showCreateForm ? (
          <div className="border-t border-slate-200 bg-slate-50 p-6">
            <div className="max-w-3xl">
              <div className="text-sm font-medium text-slate-900">Create repository</div>
              <div className="mt-4 grid gap-3 md:grid-cols-[1.2fr_1.5fr_0.8fr_auto]">
                <input aria-label="Repository name" value={createForm.name} onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))} placeholder="Repository name" className={fieldClass} />
                <input aria-label="Repository description" value={createForm.description} onChange={(e) => setCreateForm((s) => ({ ...s, description: e.target.value }))} placeholder="Short description" className={fieldClass} />
                <select aria-label="Repository visibility" value={createForm.visibility} onChange={(e) => setCreateForm((s) => ({ ...s, visibility: e.target.value }))} className={fieldClass}>
                  <option value="private">Private</option>
                  <option value="internal">Internal</option>
                  <option value="public">Public</option>
                </select>
                <div className="flex items-center gap-3">
                  <button onClick={() => void onCreateRepo()} disabled={busy === 'create-repo' || !createForm.name.trim()} className={buttonPrimary}>{busy === 'create-repo' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create</button>
                  <button onClick={() => setShowCreateForm(false)} className={buttonSecondary}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {showForkForm ? (
          <div className="border-t border-slate-200 bg-slate-50 p-6">
            <div className="max-w-4xl">
              <div className="text-sm font-medium text-slate-900">Remote repository import</div>
              <div className="mt-4 grid gap-3 md:grid-cols-[1.4fr_1fr_1fr_auto]">
                <input aria-label="Fork source URL" value={forkForm.source_url} onChange={(e) => setForkForm((s) => ({ ...s, source_url: e.target.value }))} placeholder="https://example.com/team/repo.git" className={fieldClass} />
                <input aria-label="Fork name" value={forkForm.name} onChange={(e) => setForkForm((s) => ({ ...s, name: e.target.value }))} placeholder="Repository name" className={fieldClass} />
                <input aria-label="Fork description" value={forkForm.description} onChange={(e) => setForkForm((s) => ({ ...s, description: e.target.value }))} placeholder="Short description" className={fieldClass} />
                <div className="flex items-center gap-3">
                  <button onClick={() => void onPreviewImportPlan()} disabled={busy === 'import-plan' || !forkForm.source_url.trim()} className={buttonSecondary}>{busy === 'import-plan' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />} Plan</button>
                  <button onClick={() => void onForkRepo()} disabled={busy === 'fork-repo' || !forkForm.source_url.trim() || !forkForm.name.trim()} className={buttonPrimary}>{busy === 'fork-repo' ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitFork className="h-4 w-4" />} Import</button>
                  <button onClick={() => setShowForkForm(false)} className={buttonSecondary}>Cancel</button>
                </div>
              </div>
              {importPlan ? (
                <div className="mt-4 grid gap-3 rounded-md border border-slate-200 bg-white p-4 text-sm md:grid-cols-4">
                  <MetricBlock label="Import plan" value={importPlan.allowed ? 'Allowed' : 'Blocked'} />
                  <MetricBlock label="Copy" value={importPlan.copy_strategy} multiline />
                  <MetricBlock label="Storage" value={importPlan.storage.mode} />
                  <MetricBlock label="Refs" value={String(importPlan.remote.refs.length)} />
                  <div className="md:col-span-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
                    <span className="font-medium text-slate-900">{importPlan.policy_decision.rule}</span>
                    <span className="ml-2">{importPlan.policy_decision.reason}</span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className={`${cardClass} grid gap-0 overflow-hidden xl:grid-cols-[minmax(0,1fr)_18rem]`}>
          <div className="min-w-0 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-3 text-sm text-slate-500">
              <div className="flex items-center gap-4">
                <span className="font-medium text-slate-900">Repositories</span>
                <span>{filteredRepos.length} shown</span>
              </div>
              <span>{repos.length} total</span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : filteredRepos.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-slate-600">No repositories match this filter.</div>
            ) : (
              <div>
                {filteredRepos.map((repo, index) => (
                  <Link key={repo.repo_id} to={`/repositories/${repo.repo_id}`} className={`grid items-center gap-4 px-6 py-4 transition hover:bg-slate-50 md:grid-cols-[minmax(0,1.8fr)_0.55fr_0.45fr_0.45fr] ${index > 0 ? 'border-t border-slate-200' : ''}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="truncate text-sm font-semibold text-slate-950">{repo.name}</span>
                        <span className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600">{formatVisibilityLabel(repo.visibility)}</span>
                      </div>
                      <div className="mt-1 truncate text-sm text-slate-500">{repo.description || 'No description provided.'}</div>
                    </div>
                    <RepoListDatum label="Updated" value={repo.updated_at ? formatDate(repo.updated_at) : 'recent'} />
                    <RepoListDatum label="Branches" value={String(repo.branch_count)} />
                    <RepoListDatum label="Commits" value={String(repo.commit_count)} />
                  </Link>
                ))}
              </div>
            )}
          </div>
          <aside className="border-t border-slate-200 bg-slate-50 p-6 xl:border-l xl:border-t-0">
            <div className="text-sm font-medium text-slate-900">Account policy</div>
            <div className="mt-4 space-y-4 text-sm text-slate-600">
              <InfoRow label="Default visibility" value={formatVisibilityLabel(organizationSettings?.default_repo_visibility || 'private')} />
              <InfoRow label="Reviews required" value={organizationSettings?.require_pull_request_reviews ? 'Yes' : 'No'} />
              <InfoRow label="Remote imports" value={organizationSettings?.allow_remote_imports ? 'Enabled' : 'Disabled'} />
              <InfoRow label="Account signup" value={organizationSettings?.allow_signup ? 'Enabled' : 'Disabled'} />
            </div>
          </aside>
        </div>
      </section>

    </div>
  )
}

function RepositoryDetailPage(props: {
  repoId: string
  repoDetail: WorkspaceRepoDetail | null
  repoSettings: WorkspaceRepoSettings | null
  activeTab: string
  busy: string | null
  treeRef: string
  setTreeRef: Dispatch<SetStateAction<string>>
  treePath: string
  treeEntries: Array<{ name: string; path: string; type: 'blob' | 'tree'; size?: number }>
  fileContent: string
  selectedFile: string
  commits: Array<Record<string, unknown>>
  commitIntent: WorkspaceCommitIntent | null
  selectedPrId: string | null
  setSelectedPrId: Dispatch<SetStateAction<string | null>>
  selectedPullRequest: WorkspacePullRequest | null
  prDiff: string
  prEvidence: WorkspacePullRequestEvidence | null
  codeIntelligence: WorkspaceCodeIntelligenceRecord | null
  contextfulAccess: WorkspaceContextfulAccess | null
  knowledgeProjectId: string
  knowledgeNodes: ForgeKnowledgeNode[]
  knowledgeEdges: ForgeKnowledgeEdge[]
  knowledgeLinkForm: { source_node_id: string; target_node_id: string; edge_type: string; context: string }
  setKnowledgeLinkForm: Dispatch<SetStateAction<{ source_node_id: string; target_node_id: string; edge_type: string; context: string }>>
  contextBundle: WorkspaceBranchContextBundle | null
  branchForm: { branch_name: string; from_ref: string }
  setBranchForm: Dispatch<SetStateAction<{ branch_name: string; from_ref: string }>>
  prForm: { title: string; description: string; source_branch: string; target_branch: string }
  setPrForm: Dispatch<SetStateAction<{ title: string; description: string; source_branch: string; target_branch: string }>>
  sshForm: { title: string; public_key: string }
  setSshForm: Dispatch<SetStateAction<{ title: string; public_key: string }>>
  sshKeys: WorkspaceSshKey[]
  authorizedKeys: string
  gitHttpCredentials: WorkspaceGitHttpCredential | null
  repoMembers: WorkspaceRepoMember[]
  memberForm: { email: string; display_name: string; role: WorkspaceRepoMember['role'] }
  setMemberForm: Dispatch<SetStateAction<{ email: string; display_name: string; role: WorkspaceRepoMember['role'] }>>
  repoSettingsForm: { description: string; visibility: string; default_branch: string; homepage_url: string; documentation_path: string; branch_protection_enabled: boolean; require_pull_request_reviews: boolean }
  setRepoSettingsForm: Dispatch<SetStateAction<{ description: string; visibility: string; default_branch: string; homepage_url: string; documentation_path: string; branch_protection_enabled: boolean; require_pull_request_reviews: boolean }>>
  onLoadTree: (repoId: string, ref: string, path: string) => Promise<void>
  onOpenFile: (repoId: string, path: string) => Promise<void>
  onUpsertFile: (path: string, content: string, commitMessage: string) => Promise<void>
  onDeleteFile: (path: string, commitMessage: string) => Promise<void>
  onCreateBranch: () => Promise<void>
  onCreateContextBundle: (branch: string) => Promise<void>
  onCreatePr: () => Promise<void>
  onMergePr: (pr: WorkspacePullRequest) => Promise<void>
  onActivateContextfulAccess: () => Promise<void>
  onRecordContextfulUsage: () => Promise<WorkspaceContextfulAccess>
  onCreateKnowledgeLink: () => Promise<void>
  onAddSshKey: () => Promise<void>
  onDeleteSshKey: (keyId: string) => Promise<void>
  onRotateGitHttpCredentials: () => Promise<void>
  onSaveRepoMember: () => Promise<void>
  onDeleteRepoMember: (email: string) => Promise<void>
  onSaveRepoSettings: () => Promise<void>
}) {
  const { repoId, repoDetail, repoSettings, activeTab, busy, treeRef, setTreeRef, treePath, treeEntries, fileContent, selectedFile, commits, commitIntent, selectedPrId, setSelectedPrId, selectedPullRequest, prDiff, prEvidence, codeIntelligence, contextfulAccess, knowledgeProjectId, knowledgeNodes, knowledgeEdges, knowledgeLinkForm, setKnowledgeLinkForm, contextBundle, branchForm, setBranchForm, prForm, setPrForm, sshForm, setSshForm, sshKeys, authorizedKeys, gitHttpCredentials, repoMembers, memberForm, setMemberForm, repoSettingsForm, setRepoSettingsForm, onLoadTree, onOpenFile, onUpsertFile, onDeleteFile, onCreateBranch, onCreateContextBundle, onCreatePr, onMergePr, onActivateContextfulAccess, onRecordContextfulUsage, onCreateKnowledgeLink, onAddSshKey, onDeleteSshKey, onRotateGitHttpCredentials, onSaveRepoMember, onDeleteRepoMember, onSaveRepoSettings } = props

  if (busy === 'repo-detail' && !repoDetail) {
    return <div className="flex min-h-[40vh] items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
  }

  if (!repoDetail) {
    return <div className={`${cardClass} p-8`}><h1 className="text-2xl font-semibold text-slate-950">Repository not found</h1><p className="mt-3 text-sm text-slate-600">We couldn’t load this repository in Forge.</p><Link to="/repositories" className="mt-6 inline-flex rounded-md bg-slate-950 px-4 py-2 text-sm font-medium text-white">Back to repositories</Link></div>
  }

  const tabs = [
    { key: 'overview', label: 'Overview', shortLabel: 'Overview', icon: LayoutPanelTop },
    { key: 'code', label: 'Files', shortLabel: 'Files', icon: FileCode2 },
    { key: 'branches', label: 'Branches', shortLabel: 'Branches', icon: GitBranch },
    { key: 'pulls', label: 'Pull requests', shortLabel: 'Pull requests', icon: GitPullRequest },
    { key: 'source-context', label: 'My source context', shortLabel: 'My source context', icon: Brain },
    { key: 'architect', label: 'Contextful', shortLabel: 'Contextful', icon: Network },
    { key: 'access', label: 'Access', shortLabel: 'Access', icon: KeyRound },
    { key: 'settings', label: 'Settings', shortLabel: 'Settings', icon: Settings2 },
  ]

  return (
    <div className="space-y-5">
      <section className="border-b border-slate-200 pb-0 text-slate-900">
        <div className="pb-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <Link to="/repositories" className="font-medium text-slate-600 hover:text-slate-950">Organization</Link>
              <span>/</span>
              <span className="font-medium text-slate-950">{repoDetail.name}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <h1 className="text-[30px] font-semibold text-slate-950">{repoDetail.name}</h1>
                <span className="rounded-full border border-slate-300 px-2 py-0.5 text-xs text-slate-600">{formatVisibilityLabel(repoDetail.visibility)}</span>
              </div>
              <div className="mt-2 text-sm text-slate-500">{repoDetail.description || 'No description provided.'}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => void copyText(repoDetail.http_clone_url)} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 hover:bg-slate-50"><Copy className="h-4 w-4" /> HTTPS</button>
              <button onClick={() => void copyText(repoDetail.ssh_clone_url)} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 hover:bg-slate-50"><KeyRound className="h-4 w-4" /> SSH</button>
            </div>
          </div>
          <nav className="mt-5 flex flex-wrap gap-6 text-sm">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <NavLink
                  key={tab.key}
                  to={tab.key === 'overview' ? `/repositories/${repoId}` : `/repositories/${repoId}/${tab.key}`}
                  end={tab.key === 'overview'}
                  className={({ isActive }) => `inline-flex items-center gap-2 border-b-2 pb-3 ${isActive ? 'border-[var(--forge-accent)] text-slate-950' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
                >
                  <Icon className="h-4 w-4" /> {tab.shortLabel}
                </NavLink>
              )
            })}
          </nav>
        </div>

        <div className="pt-0">
          {activeTab === 'overview' ? <RepoOverviewTab repoDetail={repoDetail} repoSettings={repoSettings} commits={commits} commitIntent={commitIntent} codeIntelligence={codeIntelligence} treeEntries={treeEntries} selectedFile={selectedFile} fileContent={fileContent} /> : null}
          {activeTab === 'code' ? <FilesTab repoId={repoId} repoDetail={repoDetail} codeIntelligence={codeIntelligence} treeRef={treeRef} setTreeRef={setTreeRef} treePath={treePath} treeEntries={treeEntries} selectedFile={selectedFile} fileContent={fileContent} busy={busy} onLoadTree={onLoadTree} onOpenFile={onOpenFile} onUpsertFile={onUpsertFile} onDeleteFile={onDeleteFile} /> : null}
          {activeTab === 'branches' ? <BranchesTab repoDetail={repoDetail} branchForm={branchForm} setBranchForm={setBranchForm} busy={busy} contextBundle={contextBundle} onCreateBranch={onCreateBranch} onCreateContextBundle={onCreateContextBundle} /> : null}
          {activeTab === 'pulls' ? <PullRequestsTab repoDetail={repoDetail} prForm={prForm} setPrForm={setPrForm} busy={busy} selectedPrId={selectedPrId} setSelectedPrId={setSelectedPrId} selectedPullRequest={selectedPullRequest} prDiff={prDiff} prEvidence={prEvidence} onCreatePr={onCreatePr} onMergePr={onMergePr} /> : null}
          {activeTab === 'source-context' ? <SourceContextPanel repoId={repoId} /> : null}
          {activeTab === 'architect' ? <ArchitectWorkTab repoDetail={repoDetail} codeIntelligence={codeIntelligence} contextfulAccess={contextfulAccess} knowledgeProjectId={knowledgeProjectId} knowledgeNodes={knowledgeNodes} knowledgeEdges={knowledgeEdges} linkForm={knowledgeLinkForm} setLinkForm={setKnowledgeLinkForm} busy={busy} onActivateContextfulAccess={onActivateContextfulAccess} onRecordContextfulUsage={onRecordContextfulUsage} onCreateKnowledgeLink={onCreateKnowledgeLink} /> : null}
          {activeTab === 'access' ? <AccessTab repoDetail={repoDetail} gitHttpCredentials={gitHttpCredentials} sshForm={sshForm} setSshForm={setSshForm} sshKeys={sshKeys} authorizedKeys={authorizedKeys} repoMembers={repoMembers} memberForm={memberForm} setMemberForm={setMemberForm} busy={busy} onAddSshKey={onAddSshKey} onDeleteSshKey={onDeleteSshKey} onSaveRepoMember={onSaveRepoMember} onDeleteRepoMember={onDeleteRepoMember} onRotateGitHttpCredentials={onRotateGitHttpCredentials} /> : null}
          {activeTab === 'settings' ? <RepoSettingsTab repoDetail={repoDetail} repoSettings={repoSettings} repoMembers={repoMembers} form={repoSettingsForm} setForm={setRepoSettingsForm} busy={busy} onSave={onSaveRepoSettings} /> : null}
        </div>
      </section>
    </div>
  )
}

function ArchitectWorkTab({
  repoDetail,
  codeIntelligence,
  contextfulAccess,
  knowledgeProjectId,
  knowledgeNodes,
  knowledgeEdges,
  linkForm,
  setLinkForm,
  busy,
  onActivateContextfulAccess,
  onRecordContextfulUsage,
  onCreateKnowledgeLink,
}: {
  repoDetail: WorkspaceRepoDetail
  codeIntelligence: WorkspaceCodeIntelligenceRecord | null
  contextfulAccess: WorkspaceContextfulAccess | null
  knowledgeProjectId: string
  knowledgeNodes: ForgeKnowledgeNode[]
  knowledgeEdges: ForgeKnowledgeEdge[]
  linkForm: { source_node_id: string; target_node_id: string; edge_type: string; context: string }
  setLinkForm: Dispatch<SetStateAction<{ source_node_id: string; target_node_id: string; edge_type: string; context: string }>>
  busy: string | null
  onActivateContextfulAccess: () => Promise<void>
  onRecordContextfulUsage: () => Promise<WorkspaceContextfulAccess>
  onCreateKnowledgeLink: () => Promise<void>
}) {
  const graphSchema = codeIntelligence?.model.graph_schema
  const sourceNode = knowledgeNodes.find((node) => node.node_id === linkForm.source_node_id)
  const targetOptions = knowledgeNodes.filter((node) => node.node_id !== linkForm.source_node_id)
  const nodeTitle = (nodeId: string) => knowledgeNodes.find((node) => node.node_id === nodeId)?.title || nodeId
  const repoTaggedNodes = knowledgeNodes.filter((node) =>
    node.tags.some((tag) => tag.includes(repoDetail.name) || tag.includes(repoDetail.git_repo_name) || tag.includes(repoDetail.repo_id)),
  )
  const visibleNodes = repoTaggedNodes.length > 0 ? repoTaggedNodes : knowledgeNodes.slice(0, 8)
  const primaryEvidence = visibleNodes[0]
  const [question, setQuestion] = useState('')
  const [taskRuns, setTaskRuns] = useState<Array<{ id: string; label: string; createdAt: string }>>([])
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Ask about ${repoDetail.name}, then turn the answer into a repository task. I will use code intelligence and the shared Contextful knowledge graph as grounding.`,
    },
  ])
  const taskTemplates = [
    { id: 'impact', label: 'Impact review', description: 'Trace likely affected files and decisions before a change lands.' },
    { id: 'plan', label: 'Implementation plan', description: 'Create a technical plan from repository structure and linked knowledge.' },
    { id: 'risk', label: 'Risk scan', description: 'Find weak spots across code paths, decisions, and graph evidence.' },
  ]
  const toolSlots = [
    { label: 'Patch runner', state: 'Next' },
    { label: 'Test planner', state: 'Next' },
    { label: 'PR assistant', state: 'Next' },
  ]

  const contextfulReady = contextfulAccess?.status === 'trial' || contextfulAccess?.status === 'active'
  const requestLimitReached = contextfulAccess?.status === 'limit_reached' || (contextfulAccess?.status === 'trial' && contextfulAccess.free_requests_remaining <= 0)

  const askArchitect = async (prompt: string) => {
    const trimmed = prompt.trim()
    if (!trimmed || !contextfulReady || requestLimitReached) return
    let nextAccess = contextfulAccess
    try {
      nextAccess = await onRecordContextfulUsage()
    } catch {
      return
    }
    setMessages((current) => [
      ...current,
      { role: 'user', content: trimmed },
      {
        role: 'assistant',
        content: `For ${repoDetail.name}, start from ${primaryEvidence?.title || 'the repository evidence'} and validate against ${codeIntelligence?.model.schema_version || 'the code model'} plus Contextful organisational knowledge. This request counts as Contextful usage for ${nextAccess?.email || 'this user'}; the next useful action is to run an impact review or connect the relevant knowledge nodes before changing code.`,
      },
    ])
    setQuestion('')
  }

  const runTask = (label: string) => {
    setTaskRuns((current) => [
      { id: `${label}-${Date.now()}`, label, createdAt: new Date().toLocaleTimeString() },
      ...current,
    ])
  }

  if (!contextfulReady || requestLimitReached) {
    return (
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <section className={`${cardClass} p-8`}>
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--forge-border)] bg-[var(--forge-panel-muted)] px-3 py-1 text-xs font-medium text-slate-600">
            <Network className="h-4 w-4" /> Contextful inside Forge
          </div>
          <h2 className="mt-4 max-w-2xl text-3xl font-semibold text-slate-950">Use organisation-wide knowledge with this repository.</h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
            Forge can host and review code on its own. Contextful is the additional intelligence layer: it brings product context, decisions, documents, conversations, and technical evidence into the same assistant. A Forge account alone is not enough for this workspace.
          </p>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <ScopeCard icon={FolderGit2} title="Code as source of truth" body="Repository structure and code intelligence ground the technical answer." />
            <ScopeCard icon={Brain} title="Organisational memory" body="Contextful adds decisions, product knowledge, documents, and operational context." />
            <ScopeCard icon={Shield} title="Usage that counts" body="Requests from Forge count as normal Contextful user requests." />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {requestLimitReached ? (
              <a href={contextfulAccess?.upgrade_url || 'https://contextful.int10h.de/pricing'} className={buttonPrimary}>Upgrade Contextful</a>
            ) : (
              <button onClick={() => void onActivateContextfulAccess()} disabled={busy === 'contextful-signup'} className={buttonPrimary}>
                {busy === 'contextful-signup' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Start free Contextful access
              </button>
            )}
            <a href={contextfulAccess?.signup_url || 'https://contextful.int10h.de/signup'} className={buttonSecondary}>Open Contextful</a>
          </div>
          <p className="mt-4 text-xs leading-6 text-slate-500">
            Free access starts with {contextfulAccess?.free_request_limit || 25} requests. Long term, this is a paid Contextful capability embedded into Forge.
          </p>
        </section>
        <aside className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">Current access</div>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <InfoRow label="Status" value={contextfulAccess?.status || 'inactive'} />
            <InfoRow label="Requests left" value={String(contextfulAccess?.free_requests_remaining || 0)} />
            <InfoRow label="Counts as Contextful user" value={contextfulAccess?.counted_as_contextful_user ? 'Yes' : 'After signup'} />
          </div>
        </aside>
      </div>
    )
  }

  const activeContextfulAccess = contextfulAccess

  return (
    <div className="space-y-5">
      <section className={`${cardClass} overflow-hidden`}>
        <div className="flex items-start justify-between gap-4 border-b border-[var(--forge-border)] bg-[var(--forge-panel)] px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <MessageSquare className="h-4 w-4 text-[var(--forge-accent)]" /> Contextful for Forge
            </div>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950">Ask with full organisational context on {repoDetail.name}</h2>
            <p className="mt-2 text-sm text-slate-600">{activeContextfulAccess.free_requests_remaining} Contextful requests remaining in this plan.</p>
          </div>
          <div className="hidden flex-wrap gap-2 text-xs md:flex">
            <span className="rounded-full bg-[var(--forge-accent-soft)] px-2.5 py-1 font-medium text-[var(--forge-accent)]">Code-aware</span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">Task-ready</span>
          </div>
        </div>

        <div className="grid min-h-[480px] items-start xl:grid-cols-[minmax(0,1fr)_21rem]">
          <div className="flex min-h-[480px] flex-col bg-white">
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
              {messages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {message.role === 'assistant' ? (
                    <div className="mt-1 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[var(--forge-accent-soft)] text-[var(--forge-accent)]">
                      <MessageSquare className="h-4 w-4" />
                    </div>
                  ) : null}
                  <div className={`max-w-[76%] rounded-lg px-4 py-3 text-sm leading-6 shadow-sm ${message.role === 'user' ? 'bg-[var(--forge-text)] text-white' : 'border border-[var(--forge-border)] bg-[var(--forge-panel-muted)] text-slate-700'}`}>
                    {message.content}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[var(--forge-border)] bg-[var(--forge-panel)] px-5 py-4">
              <div className="rounded-md border border-[var(--forge-border)] bg-white p-2 shadow-sm">
                <label htmlFor="forge-architect-question" className="sr-only">Ask Forge Architect</label>
                <div className="flex flex-col gap-2 md:flex-row md:items-end">
                  <textarea
                    id="forge-architect-question"
                    aria-label="Ask Forge Architect"
                    value={question}
                    onChange={(event) => setQuestion(event.target.value)}
                    onKeyDown={(event) => {
                      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') void askArchitect(question)
                    }}
                    placeholder="Ask Contextful about this repository and the organisation around it..."
                    className="min-h-24 flex-1 resize-none rounded-md border-0 bg-white px-3 py-3 text-base text-slate-900 outline-none placeholder:text-slate-500"
                  />
                  <button type="button" onClick={() => void askArchitect(question)} disabled={!question.trim()} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[var(--forge-accent)] bg-[var(--forge-accent)] px-5 text-sm font-semibold text-white transition hover:bg-[var(--forge-accent-strong)] disabled:cursor-not-allowed disabled:opacity-60">
                    <Send className="h-4 w-4" /> Ask
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {['What should I review first?', 'Explain the auth refresh risk.', 'Prepare a safe implementation plan.'].map((prompt) => (
                  <button key={prompt} type="button" onClick={() => void askArchitect(prompt)} className="rounded-full border border-[var(--forge-border)] bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-[var(--forge-accent)] hover:text-slate-950">
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <aside className="border-t border-[var(--forge-border)] bg-[var(--forge-panel-muted)] xl:border-l xl:border-t-0">
            <section className="border-b border-[var(--forge-border)]">
              <div className="px-5 py-4">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                  <PlayCircle className="h-4 w-4 text-[var(--forge-accent)]" /> Run repository tasks
                </div>
              </div>
              <div className="space-y-3 px-5 pb-5">
                {taskTemplates.map((task) => (
                  <div key={task.id} className="rounded-md border border-[var(--forge-border)] bg-white p-3">
                    <div className="font-medium text-slate-900">{task.label}</div>
                    <p className="mt-1 text-xs leading-5 text-slate-600">{task.description}</p>
                    <button type="button" onClick={() => runTask(task.label)} className="mt-3 inline-flex items-center gap-2 rounded-md border border-[var(--forge-border)] bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-[var(--forge-accent)] hover:text-slate-950">
                      <PlayCircle className="h-3.5 w-3.5" /> Run
                    </button>
                  </div>
                ))}
                {taskRuns.length > 0 ? (
                  <div className="border-t border-[var(--forge-border)] pt-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent runs</div>
                    {taskRuns.map((run) => (
                      <div key={run.id} className="mt-2 flex items-center justify-between rounded-md bg-white px-3 py-2 text-xs">
                        <span className="font-medium text-slate-800">{run.label}</span>
                        <span className="text-slate-500">queued {run.createdAt}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="px-5 py-4">
              <div className="text-sm font-medium text-slate-900">Future actions</div>
              <div className="mt-3 grid gap-2">
                {toolSlots.map((slot) => (
                  <button key={slot.label} type="button" className="flex items-center justify-between rounded-md border border-dashed border-[var(--forge-border)] bg-white px-3 py-2 text-left text-xs text-slate-600 transition hover:border-[var(--forge-accent)] hover:text-slate-950">
                    <span className="font-medium">{slot.label}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px]">{slot.state}</span>
                  </button>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <details className={`${cardClass} p-5`}>
          <summary className="cursor-pointer text-sm font-medium text-slate-900">Context, graph details, and model metadata</summary>
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <div className={`${subtleCardClass} p-4`}>
              <div className="text-sm font-medium text-slate-900">Shared knowledge graph</div>
              <div className="mt-3 space-y-3 text-sm text-slate-600">
                <InfoRow label="Tenant" value={repoDetail.tenant_id} />
                <InfoRow label="Project graph" value={knowledgeProjectId || 'Unmapped'} />
                <InfoRow label="Nodes" value={String(knowledgeNodes.length)} />
                <InfoRow label="Edges" value={String(knowledgeEdges.length)} />
                <InfoRow label="Schema" value={graphSchema?.schema_version || 'Code graph pending'} />
              </div>
            </div>
            <div className={`${subtleCardClass} p-4`}>
              <div className="text-sm font-medium text-slate-900">Code graph details</div>
              {codeIntelligence ? (
                <div className="mt-3 space-y-3 text-sm text-slate-600">
                  <InfoRow label="Model" value={codeIntelligence.model.schema_version} />
                  <InfoRow label="Facts" value={String(codeIntelligence.model.facts_preview.length)} />
                  <InfoRow label="Relationships" value={String(codeIntelligence.model.relationships_preview.length)} />
                  <InfoRow label="Confidence" value={`${codeIntelligence.model.confidence}%`} />
                </div>
              ) : <div className="mt-3 text-sm text-slate-500">Code intelligence is not available for this repository yet.</div>}
            </div>
          </div>
        </details>

        <section className={`${cardClass} p-5`}>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
            <Link2 className="h-4 w-4 text-[var(--forge-accent)]" /> Connect evidence
          </div>
          <div className="mt-4 space-y-3">
            <select aria-label="Source knowledge node" value={linkForm.source_node_id} onChange={(event) => {
              const source = event.target.value
              setLinkForm((current) => ({
                ...current,
                source_node_id: source,
                target_node_id: current.target_node_id === source ? knowledgeNodes.find((node) => node.node_id !== source)?.node_id || '' : current.target_node_id,
              }))
            }} className={fieldClass}>
              {knowledgeNodes.map((node) => <option key={node.node_id} value={node.node_id}>{node.title}</option>)}
            </select>
            <select aria-label="Target knowledge node" value={linkForm.target_node_id} onChange={(event) => setLinkForm((current) => ({ ...current, target_node_id: event.target.value }))} className={fieldClass}>
              {targetOptions.map((node) => <option key={node.node_id} value={node.node_id}>{node.title}</option>)}
            </select>
            <select aria-label="Technical relationship" value={linkForm.edge_type} onChange={(event) => setLinkForm((current) => ({ ...current, edge_type: event.target.value }))} className={fieldClass}>
              <option value="relates_to">Relates to</option>
              <option value="supports">Supports</option>
              <option value="refines">Refines</option>
              <option value="derived_from">Derived from</option>
              <option value="affects">Affects</option>
              <option value="mitigates">Mitigates</option>
              <option value="governed_by">Governed by</option>
            </select>
            <textarea aria-label="Technical link context" value={linkForm.context} onChange={(event) => setLinkForm((current) => ({ ...current, context: event.target.value }))} placeholder="Why are these technically connected?" className={textareaClass} />
            <button type="button" onClick={() => void onCreateKnowledgeLink()} disabled={busy === 'knowledge-link' || !sourceNode || !linkForm.target_node_id} className={buttonPrimary}>
              {busy === 'knowledge-link' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Connect
            </button>
          </div>
        </section>

        <section className={`${cardClass} overflow-hidden xl:col-span-2`}>
          <div className="border-b border-[var(--forge-border)] px-5 py-3 text-sm font-medium text-slate-900">Graph links</div>
          <div className="grid divide-y divide-[var(--forge-border)] md:grid-cols-2 md:divide-x md:divide-y-0">
            {knowledgeEdges.length > 0 ? knowledgeEdges.slice(0, 10).map((edge) => {
              const context = typeof edge.metadata?.context === 'string' ? edge.metadata.context : ''
              return (
                <div key={edge.edge_id} className="px-5 py-3 text-sm">
                  <div className="font-medium text-slate-900">{nodeTitle(edge.source_node_id)}</div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-slate-500">{edge.edge_type}</div>
                  <div className="mt-1 text-slate-700">{nodeTitle(edge.target_node_id)}</div>
                  {context ? <div className="mt-2 text-xs text-slate-500">{context}</div> : null}
                </div>
              )
            }) : <div className="px-5 py-8 text-sm text-slate-500">No graph links yet.</div>}
          </div>
        </section>
      </section>
    </div>
  )
}

function ArchitectTab({
  repoDetail,
  codeIntelligence,
  knowledgeProjectId,
  knowledgeNodes,
  knowledgeEdges,
  linkForm,
  setLinkForm,
  busy,
  onCreateKnowledgeLink,
}: {
  repoDetail: WorkspaceRepoDetail
  codeIntelligence: WorkspaceCodeIntelligenceRecord | null
  knowledgeProjectId: string
  knowledgeNodes: ForgeKnowledgeNode[]
  knowledgeEdges: ForgeKnowledgeEdge[]
  linkForm: { source_node_id: string; target_node_id: string; edge_type: string; context: string }
  setLinkForm: Dispatch<SetStateAction<{ source_node_id: string; target_node_id: string; edge_type: string; context: string }>>
  busy: string | null
  onCreateKnowledgeLink: () => Promise<void>
}) {
  const graphSchema = codeIntelligence?.model.graph_schema
  const sourceNode = knowledgeNodes.find((node) => node.node_id === linkForm.source_node_id)
  const targetOptions = knowledgeNodes.filter((node) => node.node_id !== linkForm.source_node_id)
  const nodeTitle = (nodeId: string) => knowledgeNodes.find((node) => node.node_id === nodeId)?.title || nodeId
  const repoTaggedNodes = knowledgeNodes.filter((node) =>
    node.tags.some((tag) => tag.includes(repoDetail.name) || tag.includes(repoDetail.git_repo_name) || tag.includes(repoDetail.repo_id)),
  )
  const visibleNodes = repoTaggedNodes.length > 0 ? repoTaggedNodes : knowledgeNodes.slice(0, 8)

  return (
    <div className="space-y-6">
      <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
            <Network className="h-4 w-4 text-[var(--forge-accent)]" /> Technical solution
          </div>
          <h2 className="mt-3 text-2xl font-semibold text-slate-950">Architecture for {repoDetail.name}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricBlock label="Code model" value={codeIntelligence?.model.schema_version || 'Unavailable'} multiline />
            <MetricBlock label="Project graph" value={knowledgeProjectId || 'Unmapped'} multiline />
            <MetricBlock label="Facts" value={String(codeIntelligence?.model.facts_preview.length || 0)} />
            <MetricBlock label="Relationships" value={String(codeIntelligence?.model.relationships_preview.length || 0)} />
          </div>
        </div>
        <aside className={`${subtleCardClass} p-4`}>
          <div className="text-sm font-medium text-slate-900">Shared knowledge graph</div>
          <div className="mt-3 space-y-3 text-sm text-slate-600">
            <InfoRow label="Tenant" value={repoDetail.tenant_id} />
            <InfoRow label="Nodes" value={String(knowledgeNodes.length)} />
            <InfoRow label="Edges" value={String(knowledgeEdges.length)} />
            <InfoRow label="Schema" value={graphSchema?.schema_version || 'Code graph pending'} />
          </div>
        </aside>
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <div className={`${cardClass} overflow-hidden`}>
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <Brain className="h-4 w-4 text-[var(--forge-accent)]" /> Evidence and decisions
            </div>
            <span className="text-xs text-slate-500">{visibleNodes.length} shown</span>
          </div>
          <div className="divide-y divide-slate-200">
            {visibleNodes.length > 0 ? visibleNodes.map((node) => (
              <div key={node.node_id} className="px-5 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-slate-950">{node.title}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{node.node_type}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{node.status}</span>
                </div>
                <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">{node.content}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {node.tags.slice(0, 4).map((tag) => (
                    <span key={tag} className="rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-500">{tag}</span>
                  ))}
                </div>
              </div>
            )) : (
              <div className="px-5 py-8 text-sm text-slate-500">No project knowledge has been mapped to this repository yet.</div>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <section className={`${cardClass} p-5`}>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <Link2 className="h-4 w-4 text-[var(--forge-accent)]" /> Manual technical link
            </div>
            <div className="mt-4 space-y-3">
              <select
                aria-label="Source knowledge node"
                value={linkForm.source_node_id}
                onChange={(event) => {
                  const source = event.target.value
                  setLinkForm((current) => ({
                    ...current,
                    source_node_id: source,
                    target_node_id: current.target_node_id === source ? knowledgeNodes.find((node) => node.node_id !== source)?.node_id || '' : current.target_node_id,
                  }))
                }}
                className={fieldClass}
              >
                {knowledgeNodes.map((node) => <option key={node.node_id} value={node.node_id}>{node.title}</option>)}
              </select>
              <select
                aria-label="Target knowledge node"
                value={linkForm.target_node_id}
                onChange={(event) => setLinkForm((current) => ({ ...current, target_node_id: event.target.value }))}
                className={fieldClass}
              >
                {targetOptions.map((node) => <option key={node.node_id} value={node.node_id}>{node.title}</option>)}
              </select>
              <select
                aria-label="Technical relationship"
                value={linkForm.edge_type}
                onChange={(event) => setLinkForm((current) => ({ ...current, edge_type: event.target.value }))}
                className={fieldClass}
              >
                <option value="relates_to">Relates to</option>
                <option value="supports">Supports</option>
                <option value="refines">Refines</option>
                <option value="derived_from">Derived from</option>
                <option value="affects">Affects</option>
                <option value="mitigates">Mitigates</option>
                <option value="governed_by">Governed by</option>
              </select>
              <textarea
                aria-label="Technical link context"
                value={linkForm.context}
                onChange={(event) => setLinkForm((current) => ({ ...current, context: event.target.value }))}
                placeholder="Why are these technically connected?"
                className={textareaClass}
              />
              <button
                type="button"
                onClick={() => void onCreateKnowledgeLink()}
                disabled={busy === 'knowledge-link' || !sourceNode || !linkForm.target_node_id}
                className={buttonPrimary}
              >
                {busy === 'knowledge-link' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                Connect
              </button>
            </div>
          </section>

          <section className={`${cardClass} overflow-hidden`}>
            <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-900">Graph links</div>
            <div className="max-h-80 divide-y divide-slate-200 overflow-y-auto">
              {knowledgeEdges.length > 0 ? knowledgeEdges.slice(0, 10).map((edge) => {
                const context = typeof edge.metadata?.context === 'string' ? edge.metadata.context : ''
                return (
                  <div key={edge.edge_id} className="px-5 py-3 text-sm">
                    <div className="font-medium text-slate-900">{nodeTitle(edge.source_node_id)}</div>
                    <div className="mt-1 text-xs uppercase tracking-wide text-slate-500">{edge.edge_type}</div>
                    <div className="mt-1 text-slate-700">{nodeTitle(edge.target_node_id)}</div>
                    {context ? <div className="mt-2 text-xs text-slate-500">{context}</div> : null}
                  </div>
                )
              }) : (
                <div className="px-5 py-8 text-sm text-slate-500">No graph links yet.</div>
              )}
            </div>
          </section>
        </div>
      </section>

      <section className={`${cardClass} p-5`}>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
          <FileCode2 className="h-4 w-4 text-[var(--forge-accent)]" /> Code graph details
        </div>
        {codeIntelligence ? (
          <div className="mt-4 grid gap-4 xl:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Engines</div>
              <div className="mt-3 space-y-2">
                {codeIntelligence.model.engines.map((engine) => (
                  <div key={`${engine.engine}-${engine.role}`} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <span className="font-medium text-slate-900">{engine.engine}</span>
                    <span className="text-slate-500">{engine.status} · {engine.produced_facts} facts</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Relationships</div>
              <div className="mt-3 space-y-2">
                {codeIntelligence.model.relationships_preview.slice(0, 5).map((relationship) => (
                  <div key={relationship.relationship_id} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <div className="font-medium text-slate-900">{relationship.relationship_type}</div>
                    <div className="mt-1 text-xs text-slate-500">{relationship.ontology?.edge_type || 'unmapped'} · {relationship.confidence}% confidence</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 text-sm text-slate-500">Code intelligence is not available for this repository yet.</div>
        )}
      </section>
    </div>
  )
}

function RepoOverviewTab({ repoDetail, repoSettings, commits, commitIntent, codeIntelligence, treeEntries, selectedFile, fileContent }: { repoDetail: WorkspaceRepoDetail; repoSettings: WorkspaceRepoSettings | null; commits: Array<Record<string, unknown>>; commitIntent: WorkspaceCommitIntent | null; codeIntelligence: WorkspaceCodeIntelligenceRecord | null; treeEntries: Array<{ name: string; path: string; type: 'blob' | 'tree'; size?: number }>; selectedFile: string; fileContent: string }) {
  const branches = repoDetail.branches.slice(0, 6)
  const openPrs = repoDetail.pull_requests.filter((pr) => pr.status === 'open')
  const latestCommit = commits[0]
  const readmePath = selectedFile || repoSettings?.documentation_path || 'README.md'
  const graphSchema = codeIntelligence?.model.graph_schema

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
      <div className="space-y-4">
        {!repoSettings?.branch_protection_enabled ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-slate-900">Your default branch isn’t protected</div>
                <p className="mt-1 text-sm text-slate-700">Protect this branch from force pushes or require reviews before merge.</p>
              </div>
              <Link to={`/repositories/${repoDetail.repo_id}/settings`} className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50">Protect branch</Link>
            </div>
          </div>
        ) : null}
        <div className={`${cardClass} overflow-hidden`}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
              <span className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 font-medium text-slate-900"><GitBranch className="h-4 w-4" /> {repoDetail.default_branch}</span>
              <span>{repoDetail.branch_count} branches</span>
              <span>0 tags</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Link to={`/repositories/${repoDetail.repo_id}/code`} className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-700 hover:bg-slate-50">Go to file</Link>
              <Link to={`/repositories/${repoDetail.repo_id}/code`} className="inline-flex items-center rounded-md border border-slate-900 bg-slate-900 px-3 py-2 font-medium text-white hover:bg-slate-800">Code</Link>
            </div>
          </div>
          {latestCommit ? (
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 border-b border-slate-200 px-4 py-3 text-sm">
              <div className="min-w-0 truncate text-slate-700">
                <span className="font-medium text-slate-900">{commitAuthor(latestCommit)}</span>{' '}
                <span>{String(latestCommit.message || 'Latest commit')}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-500">
                <span>{latestCommit.sha ? String(latestCommit.sha).slice(0, 7) : ''}</span>
                <span>{latestCommit?.authored_at ? formatDate(String(latestCommit.authored_at)) : ''}</span>
              </div>
            </div>
          ) : null}
          <div>
            {treeEntries.map((entry, index) => (
              <div key={entry.path} className={`grid grid-cols-[minmax(0,1.2fr)_0.9fr_0.5fr] gap-4 px-4 py-3 text-sm ${index > 0 ? 'border-t border-slate-200' : ''}`}>
                <div className="flex min-w-0 items-center gap-3 text-slate-900">
                  {entry.type === 'tree' ? <Folder className="h-4 w-4 shrink-0 text-slate-500" /> : <FileText className="h-4 w-4 shrink-0 text-slate-500" />}
                  <span className="truncate">{entry.name || entry.path || 'Untitled entry'}</span>
                </div>
                <div className="truncate text-slate-500">{latestCommit ? String(latestCommit.message || 'Latest commit') : entry.type === 'tree' ? 'Directory' : 'File'}</div>
                <div className="text-right text-slate-500">{latestCommit?.authored_at ? formatDate(String(latestCommit.authored_at)) : ''}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${cardClass} overflow-hidden`}>
          <div className="border-b border-slate-200 px-4 py-3 text-sm font-medium text-slate-900">{readmePath}</div>
          <div className="p-5">
            {selectedFile ? (
              <article className="whitespace-pre-wrap text-sm leading-7 text-slate-800">{fileContent || 'Choose a file from the repository tree.'}</article>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-sm text-slate-600">Add a README or docs overview to give this repository a useful front page.</div>
            )}
          </div>
        </div>
      </div>

      <aside className="space-y-4">
        <div className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">About</div>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <div>{repoDetail.description || 'No description, website, or topics provided.'}</div>
            <InfoRow label="Activity" value={`${repoDetail.commit_count} commits`} />
            <InfoRow label="Branches" value={`${repoDetail.branch_count}`} />
            <InfoRow label="Pull requests" value={`${repoDetail.pull_requests.length}`} />
          </div>
        </div>
        <div className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">Contributors</div>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            {commits.slice(0, 4).map((commit, index) => (
              <div key={String(commit.sha || index)} className="flex items-center justify-between gap-3">
                <span className="font-medium text-slate-900">{commitAuthor(commit)}</span>
                <span>{commit.authored_at ? formatDate(String(commit.authored_at)) : 'recent'}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={`${cardClass} p-5`}>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Shield className="h-4 w-4" /> Commit intent</div>
          {commitIntent ? (
            <div className="mt-4 space-y-3 text-sm text-slate-600">
              <InfoRow label="State" value={commitIntent.validation_state} />
              <InfoRow label="Evidence" value={`${commitIntent.evidence_refs.length} refs`} />
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="font-medium text-slate-900">{String(commitIntent.commit.subject || commitIntent.commit.message || 'Latest commit')}</div>
                <div className="mt-1 leading-6">{commitIntent.why}</div>
              </div>
            </div>
          ) : <div className="mt-4 text-sm text-slate-500">No commit evidence yet.</div>}
        </div>
        <div className={`${cardClass} p-5`}>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><FileCode2 className="h-4 w-4" /> Code intelligence</div>
          {codeIntelligence ? (
            <div className="mt-4 space-y-3 text-sm text-slate-600">
              <InfoRow label="Model" value={codeIntelligence.model.schema_version} />
              <InfoRow label="Graph" value={graphSchema?.schema_version || 'unified'} />
              <InfoRow label="Confidence" value={`${codeIntelligence.model.confidence}%`} />
              <InfoRow label="Reconciliation" value={`${codeIntelligence.model.engine_reconciliation}%`} />
              <InfoRow label="Facts" value={`${codeIntelligence.model.facts_preview.length}`} />
              <InfoRow label="Relations" value={`${codeIntelligence.model.relationships_preview.length}`} />
              <InfoRow label="Raw content" value={codeIntelligence.model.governance.raw_source_content_included ? 'Included' : 'Excluded'} />
            </div>
          ) : <div className="mt-4 text-sm text-slate-500">Code intelligence is not available yet.</div>}
        </div>
        <div className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">Open pull requests</div>
          <div className="mt-4 space-y-3">
            {openPrs.length === 0 ? <div className="text-sm text-slate-500">No open pull requests.</div> : openPrs.map((pr) => <div key={pr.pr_id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm"><div className="font-medium text-slate-900">{pr.title}</div><div className="mt-1 text-slate-500">{pr.source_branch} → {pr.target_branch}</div></div>)}
          </div>
        </div>
        <div className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">Active branches</div>
          <div className="mt-4 space-y-2">
            {branches.map((branch) => <div key={branch} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{branch}</div>)}
          </div>
        </div>
      </aside>
    </div>
  )
}

function FilesTab({ repoId, repoDetail, codeIntelligence, treeRef, setTreeRef, treePath, treeEntries, selectedFile, fileContent, busy, onLoadTree, onOpenFile, onUpsertFile, onDeleteFile }: any) {
  const segments = treePath ? treePath.split('/') : []
  const isDocumentPreview = selectedFile.toLowerCase().endsWith('.md') || selectedFile.toLowerCase().endsWith('.txt')
  const navigationFacts = codeNavigationFacts(codeIntelligence)
  const [draftPath, setDraftPath] = useState('')
  const [draftContent, setDraftContent] = useState('')
  const [draftCommitMessage, setDraftCommitMessage] = useState('Update file')
  const [showCreatePanel, setShowCreatePanel] = useState(false)
  const draftPathRef = useRef('')
  const draftContentRef = useRef('')
  const draftCommitRef = useRef('Update file')

  useEffect(() => {
    if (showCreatePanel && !selectedFile) return
    setDraftPath(selectedFile || '')
    setDraftContent(fileContent || '')
    setDraftCommitMessage(selectedFile ? `Update ${selectedFile}` : 'Create file')
    draftPathRef.current = selectedFile || ''
    draftContentRef.current = fileContent || ''
    draftCommitRef.current = selectedFile ? `Update ${selectedFile}` : 'Create file'
  }, [selectedFile, fileContent, showCreatePanel])

  return (
    <div className="space-y-6">
      <section className={`${cardClass} overflow-hidden`}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><FileCode2 className="h-4 w-4" /> Code navigation</div>
          {codeIntelligence ? <div className="text-sm text-slate-500">{codeIntelligence.model.graph_schema?.schema_version || codeIntelligence.model.schema_version}</div> : null}
        </div>
        {codeIntelligence ? (
          <div className="grid gap-0 md:grid-cols-[1fr_1fr]">
            <div className="border-b border-slate-200 p-4 md:border-b-0 md:border-r">
              <div className="grid gap-3 sm:grid-cols-3">
                <MetricBlock label="Engines" value={String(codeIntelligence.model.engines.length)} />
                <MetricBlock label="Symbols" value={String(codeIntelligence.model.symbols)} />
                <MetricBlock label="Config refs" value={String(codeIntelligence.model.config_references)} />
              </div>
              <div className="mt-4 grid gap-2">
                {codeIntelligence.model.engines.map((engine) => (
                  <div key={engine.engine} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-medium text-slate-900">{engine.engine}</span>
                    <span className="text-slate-600">{engine.status} · {engine.confidence}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4">
              <div className="mb-3 text-xs uppercase tracking-wide text-slate-500">Unified graph entries</div>
              <div className="grid gap-2">
                {navigationFacts.map((fact) => (
                  <button
                    key={fact.fact_id}
                    onClick={() => fact.path ? void onOpenFile(repoId, fact.path) : undefined}
                    disabled={!fact.path}
                    className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm transition hover:bg-white disabled:cursor-default disabled:hover:bg-slate-50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-slate-900">{fact.name}</span>
                      <span className="block truncate text-xs text-slate-500">{fact.path || fact.evidence_ref}</span>
                    </span>
                    <span className="text-right text-xs text-slate-500">{fact.language || fact.fact_type}<br />{fact.confidence}%</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-4 py-6 text-sm text-slate-500">Code navigation is waiting for the shared intelligence model.</div>
        )}
      </section>
      <section className={`${cardClass} overflow-hidden`}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><FileCode2 className="h-4 w-4" /> Files</div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setShowCreatePanel((current: boolean) => !current)
                const nextPath = treePath ? `${treePath}/new-file.txt` : 'new-file.txt'
                setDraftPath(nextPath)
                setDraftContent('')
                setDraftCommitMessage('Create file')
                draftPathRef.current = nextPath
                draftContentRef.current = ''
                draftCommitRef.current = 'Create file'
              }}
              className={buttonSecondary}
            >
              <Plus className="h-4 w-4" /> New file
            </button>
            <select aria-label="Branch or ref" value={treeRef} onChange={(e) => { const next = e.target.value; setTreeRef(next); void onLoadTree(repoId, next, '') }} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
              {repoDetail.branches.map((branch: string) => <option key={branch} value={branch}>{branch}</option>)}
            </select>
          </div>
        </div>
        <div className="border-b border-slate-200 px-4 py-3 text-xs text-slate-500">
          <button onClick={() => void onLoadTree(repoId, treeRef, '')} className="mr-2 hover:text-slate-900">root</button>
          {segments.map((segment: string, index: number) => {
            const path = segments.slice(0, index + 1).join('/')
            return <button key={path} onClick={() => void onLoadTree(repoId, treeRef, path)} className="mr-2 hover:text-slate-900">/{segment}</button>
          })}
        </div>
        {showCreatePanel ? (
          <div className="border-b border-slate-200 bg-slate-50 p-4">
            <div className="grid gap-3">
              <input aria-label="New file path" value={draftPath} onChange={(e) => { setDraftPath(e.target.value); draftPathRef.current = e.target.value }} placeholder="src/new-file.ts" className={fieldClass} />
              <input aria-label="File commit message" value={draftCommitMessage} onChange={(e) => { setDraftCommitMessage(e.target.value); draftCommitRef.current = e.target.value }} placeholder="Create file" className={fieldClass} />
              <textarea aria-label="New file content" value={draftContent} onChange={(e) => { setDraftContent(e.target.value); draftContentRef.current = e.target.value }} placeholder="Start writing..." className={`${textareaClass} min-h-40 font-mono text-xs`} />
                <div className="flex items-center gap-3">
                  <button onClick={async () => { await onUpsertFile(draftPathRef.current, draftContentRef.current, draftCommitRef.current); setShowCreatePanel(false) }} disabled={busy === 'save-file' || !draftPath.trim()} className={buttonPrimary}>{busy === 'save-file' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create file</button>
                  <button onClick={() => setShowCreatePanel(false)} className={buttonSecondary}>Cancel</button>
                </div>
            </div>
          </div>
        ) : null}
        <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div>
            {busy === 'tree' ? <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /></div> : null}
            {treeEntries.length === 0 && busy !== 'tree' ? <div className="px-4 py-8 text-sm text-slate-600">There’s nothing at this path yet.</div> : null}
            {treeEntries.map((entry: any, index: number) => (
              <button key={entry.path} onClick={() => entry.type === 'tree' ? void onLoadTree(repoId, treeRef, entry.path) : void onOpenFile(repoId, entry.path)} className={`grid w-full grid-cols-[1.5fr_0.6fr] gap-4 px-4 py-3 text-left text-sm transition hover:bg-slate-50 ${index > 0 ? 'border-t border-slate-200' : ''}`}>
                <span className="flex items-center gap-3 font-medium text-slate-900">{entry.type === 'tree' ? <Folder className="h-4 w-4 text-slate-500" /> : <FileText className="h-4 w-4 text-slate-500" />}{entry.name || entry.path || 'Untitled entry'}</span>
                <span className="text-slate-500">{entry.type === 'tree' ? 'Directory' : 'File'}</span>
              </button>
            ))}
          </div>
          <div className="border-t border-slate-200 xl:border-l xl:border-t-0">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div className="text-sm font-medium text-slate-900">Preview</div>
              {selectedFile ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => void onUpsertFile(draftPathRef.current || selectedFile, draftContentRef.current, draftCommitRef.current || `Update ${selectedFile}`)}
                    disabled={busy === 'save-file' || !draftPath.trim()}
                    className={buttonSecondary}
                  >
                    {busy === 'save-file' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Save
                  </button>
                  <button
                    onClick={() => void onDeleteFile(selectedFile, `Delete ${selectedFile}`)}
                    disabled={busy === 'delete-file'}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {busy === 'delete-file' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Delete
                  </button>
                </div>
              ) : null}
            </div>
            <div className="p-4">
              <div className="mb-3 text-xs uppercase tracking-wide text-slate-500">{selectedFile || 'Select a file from the repository tree'}</div>
              {selectedFile ? <input aria-label="Selected file path" value={draftPath} onChange={(e) => { setDraftPath(e.target.value); draftPathRef.current = e.target.value }} className={`${fieldClass} mb-3`} /> : null}
              {selectedFile ? <input aria-label="Selected file commit message" value={draftCommitMessage} onChange={(e) => { setDraftCommitMessage(e.target.value); draftCommitRef.current = e.target.value }} className={`${fieldClass} mb-3`} /> : null}
              {busy === 'file' ? <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /></div> : (
                <textarea
                  aria-label="File editor"
                  value={draftContent}
                  onChange={(e) => { setDraftContent(e.target.value); draftContentRef.current = e.target.value }}
                  readOnly={!selectedFile}
                  className={`min-h-[34rem] w-full rounded-md border border-slate-200 bg-white p-4 font-mono text-sm leading-7 text-slate-900 outline-none ${!selectedFile ? 'cursor-not-allowed bg-slate-50 text-slate-500' : ''}`}
                />
              )}
              {!selectedFile ? <div className="mt-3 text-sm text-slate-500">{isDocumentPreview ? 'Choose a document to preview it.' : 'Choose a file to inspect or edit its contents.'}</div> : null}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

function BranchesTab({ repoDetail, branchForm, setBranchForm, busy, contextBundle, onCreateBranch, onCreateContextBundle }: any) {
  return (
    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <section className={`${cardClass} p-6`}>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><GitBranch className="h-4 w-4" /> Create a branch</div>
        <div className="mt-4 grid gap-3">
          <input aria-label="Branch name" value={branchForm.branch_name} onChange={(e) => setBranchForm((s: any) => ({ ...s, branch_name: e.target.value }))} placeholder="feature/customer-launch" className={fieldClass} />
          <select aria-label="Branch base ref" value={branchForm.from_ref} onChange={(e) => setBranchForm((s: any) => ({ ...s, from_ref: e.target.value }))} className={fieldClass}>
            {repoDetail.branches.map((branch: string) => <option key={branch} value={branch}>{branch}</option>)}
          </select>
          <button onClick={() => void onCreateBranch()} disabled={busy === 'create-branch'} className={buttonPrimary}>{busy === 'create-branch' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create branch</button>
        </div>
      </section>
      <section className={`${cardClass} overflow-hidden`}>
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-medium text-slate-900">Branches</div>
        <div>
          {repoDetail.branches.map((branch: string, index: number) => (
            <div key={branch} className={`flex items-center justify-between px-4 py-3 text-sm ${index > 0 ? 'border-t border-slate-200' : ''}`}>
              <div>
                <div className="font-medium text-slate-900">{branch}</div>
                <div className="mt-1 text-slate-500">{branch === repoDetail.default_branch ? 'Default branch' : 'Feature or release branch'}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => void onCreateContextBundle(branch)} disabled={busy === `context-bundle-${branch}`} className={buttonSecondary}>{busy === `context-bundle-${branch}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />} Bundle</button>
                <span className={`rounded-full px-3 py-1 text-xs ${branch === repoDetail.default_branch ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>{branch === repoDetail.default_branch ? 'default' : 'active'}</span>
              </div>
            </div>
          ))}
        </div>
        {contextBundle ? (
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-4 text-sm">
            <div className="font-medium text-slate-900">Context bundle</div>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <MetricBlock label="Branch" value={contextBundle.branch} multiline />
              <MetricBlock label="Evidence" value={`${contextBundle.evidence_refs.length} refs`} />
              <MetricBlock label="Raw content" value={contextBundle.raw_content_included ? 'Included' : 'Excluded'} />
            </div>
            <div className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-3 text-slate-700">{contextBundle.bundle_id}</div>
          </div>
        ) : null}
      </section>
    </div>
  )
}

function PullRequestsTab({ repoDetail, prForm, setPrForm, busy, selectedPrId, setSelectedPrId, selectedPullRequest, prDiff, prEvidence, onCreatePr, onMergePr }: any) {
  return (
    <div className="grid gap-6 xl:grid-cols-[0.88fr_1.12fr]">
      <section className={`${cardClass} p-6`}>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><GitPullRequest className="h-4 w-4" /> Open a pull request</div>
        <div className="mt-4 grid gap-3">
          <input aria-label="Pull request title" value={prForm.title} onChange={(e) => setPrForm((s: any) => ({ ...s, title: e.target.value }))} placeholder="Review repository bootstrap and clone policies" className={fieldClass} />
          <textarea aria-label="Pull request description" value={prForm.description} onChange={(e) => setPrForm((s: any) => ({ ...s, description: e.target.value }))} placeholder="Describe what this change does" className={textareaClass} />
          <div className="grid gap-3 sm:grid-cols-2">
            <select aria-label="Source branch" value={prForm.source_branch} onChange={(e) => setPrForm((s: any) => ({ ...s, source_branch: e.target.value }))} className={fieldClass}>{repoDetail.branches.map((branch: string) => <option key={branch} value={branch}>{branch}</option>)}</select>
            <select aria-label="Target branch" value={prForm.target_branch} onChange={(e) => setPrForm((s: any) => ({ ...s, target_branch: e.target.value }))} className={fieldClass}>{repoDetail.branches.map((branch: string) => <option key={branch} value={branch}>{branch}</option>)}</select>
          </div>
          <button onClick={() => void onCreatePr()} disabled={busy === 'create-pr'} className={buttonPrimary}>{busy === 'create-pr' ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitPullRequest className="h-4 w-4" />} Create pull request</button>
        </div>
        <div className="mt-6 space-y-3">
          {(repoDetail.pull_requests || []).map((pr: WorkspacePullRequest) => (
            <button key={pr.pr_id} onClick={() => setSelectedPrId(pr.pr_id)} className={`w-full rounded-md border px-4 py-3 text-left transition ${selectedPrId === pr.pr_id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">{pr.title}</div>
                  <div className={`mt-1 text-xs ${selectedPrId === pr.pr_id ? 'text-slate-300' : 'text-slate-500'}`}>{pr.source_branch} → {pr.target_branch}</div>
                </div>
                <span className={`rounded-full px-2 py-1 text-[11px] ${pr.status === 'merged' ? 'bg-emerald-500/20 text-emerald-200' : selectedPrId === pr.pr_id ? 'bg-white/10 text-white' : 'bg-white text-slate-600'}`}>{pr.status}</span>
              </div>
            </button>
          ))}
        </div>
      </section>
      <section className={`${cardClass} p-6`}>
        {selectedPullRequest ? (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold text-slate-950">{selectedPullRequest.title}</h2>
                <p className="mt-2 text-sm leading-7 text-slate-600">{selectedPullRequest.description || 'No description provided yet.'}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs ${selectedPullRequest.status === 'merged' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>{selectedPullRequest.status}</span>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm text-slate-600">
              <MetricBlock label="Source" value={selectedPullRequest.source_branch} />
              <MetricBlock label="Target" value={selectedPullRequest.target_branch} />
              <MetricBlock label="Opened" value={formatDate(selectedPullRequest.created_at)} />
            </div>
            <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3"><div className="text-sm font-medium text-slate-900">Diff preview</div>{busy === `diff-${selectedPullRequest.pr_id}` ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : null}</div>
              <pre className="mt-3 max-h-[28rem] overflow-auto whitespace-pre-wrap rounded-md bg-white p-4 text-xs leading-6 text-slate-900">{prDiff || 'Select a pull request to inspect its diff.'}</pre>
            </div>
            <div className="mt-5 rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Shield className="h-4 w-4" /> Evidence</div>
              {prEvidence ? (
                <div className="mt-4 grid gap-3">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <MetricBlock label="Policy" value={prEvidence.policy_decisions[0]?.allowed ? 'Allowed' : 'Blocked'} />
                    <MetricBlock label="Citations" value={String(prEvidence.citations.length)} />
                    <MetricBlock label="Transcripts" value={prEvidence.raw_transcripts_exposed ? 'Visible' : 'Hidden'} />
                  </div>
                  {prEvidence.code_intelligence ? (
                    <div className="grid gap-3 sm:grid-cols-3">
                      <MetricBlock label="Code model" value={prEvidence.code_intelligence.schema_version} multiline />
                      <MetricBlock label="Graph" value={prEvidence.code_intelligence.graph_schema_version || 'unified'} multiline />
                      <MetricBlock label="Raw source" value={prEvidence.code_intelligence.raw_source_content_included ? 'Included' : 'Excluded'} />
                    </div>
                  ) : null}
                  <div className="grid gap-2">
                    {prEvidence.citations.slice(0, 4).map((citation) => (
                      <div key={citation.citationId} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-slate-900">{citation.sourceType}</span>
                          <span className="min-w-0 truncate text-right text-slate-600">{citation.supportStatus}</span>
                        </div>
                        <div className="mt-1 truncate text-xs text-slate-500">{citation.storageMode} · {citation.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <div className="mt-4 text-sm text-slate-500">Evidence is loading.</div>}
            </div>
            {selectedPullRequest.status === 'merged' ? (
              <div className="mt-5 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                Pull request merged. Merge commit: <span className="font-mono">{selectedPullRequest.merge_commit || 'recorded'}</span>
              </div>
            ) : null}
            <div className="mt-5 flex items-center justify-between gap-4">
              {selectedPullRequest.status === 'open' ? <button onClick={() => void onMergePr(selectedPullRequest)} disabled={busy === `merge-${selectedPullRequest.pr_id}`} className={buttonPrimary}>{busy === `merge-${selectedPullRequest.pr_id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Merge pull request</button> : <div className="text-sm text-slate-600">Merge commit: {selectedPullRequest.merge_commit || 'recorded'}</div>}
              <div className="text-sm text-slate-500">Opened by {selectedPullRequest.created_by}</div>
            </div>
          </>
        ) : <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-sm text-slate-600">Select a pull request to review it.</div>}
      </section>
    </div>
  )
}

function AccessTab({ repoDetail, gitHttpCredentials, sshForm, setSshForm, sshKeys, authorizedKeys, repoMembers, memberForm, setMemberForm, busy, onAddSshKey, onDeleteSshKey, onSaveRepoMember, onDeleteRepoMember, onRotateGitHttpCredentials }: any) {
  return (
    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <section className={`${cardClass} p-6`}>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><KeyRound className="h-4 w-4" /> Personal SSH access</div>
        <p className="mt-2 text-sm leading-6 text-slate-600">SSH keys are user-scoped. They follow your Forge account across repositories you can access and are enforced through the Forge session plus git-server authorization.</p>
        <div className="mt-4 grid gap-3">
          <input aria-label="SSH key title" value={sshForm.title} onChange={(e) => setSshForm((s: any) => ({ ...s, title: e.target.value }))} placeholder="Laptop key" className={fieldClass} />
          <textarea aria-label="SSH public key" value={sshForm.public_key} onChange={(e) => setSshForm((s: any) => ({ ...s, public_key: e.target.value }))} placeholder="ssh-ed25519 AAAA..." className={`${textareaClass} font-mono`} />
          <button onClick={() => void onAddSshKey()} disabled={busy === 'ssh-add'} className={buttonPrimary}>{busy === 'ssh-add' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add SSH key</button>
        </div>
      </section>
      <section className="space-y-6">
        <div className={`${cardClass} p-6`}>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Copy className="h-4 w-4" /> HTTPS Git credentials</div>
          <div className="mt-4 grid gap-3">
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Username</div>
              <div className="flex gap-2">
                <input readOnly aria-label="Git HTTP username" value={gitHttpCredentials?.username || ''} className={`${fieldClass} font-mono`} />
                <button onClick={() => void copyText(gitHttpCredentials?.username || '')} className={buttonSecondary}>Copy</button>
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Password</div>
              <div className="flex gap-2">
                <input readOnly aria-label="Git HTTP password" value={gitHttpCredentials?.password || ''} className={`${fieldClass} font-mono`} />
                <button onClick={() => void copyText(gitHttpCredentials?.password || '')} className={buttonSecondary}>Copy</button>
                <button onClick={() => void onRotateGitHttpCredentials()} disabled={busy === 'rotate-http-credentials'} className={buttonSecondary}>{busy === 'rotate-http-credentials' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rotate'}</button>
              </div>
            </div>
          </div>
        </div>
        <div className={`${cardClass} p-6`}>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Shield className="h-4 w-4" /> Repository members</div>
          <p className="mt-2 text-sm leading-6 text-slate-600">Repository access is repo-local. Keycloak authenticates the user, while Forge policy assigns the repo role: owner, maintainer, writer, or reader.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-[1.1fr_1fr_0.9fr_auto]">
            <input aria-label="Repository member email" value={memberForm.email} onChange={(e) => setMemberForm((s: any) => ({ ...s, email: e.target.value }))} placeholder="teammate@company.com" className={fieldClass} />
            <input aria-label="Repository member display name" value={memberForm.display_name} onChange={(e) => setMemberForm((s: any) => ({ ...s, display_name: e.target.value }))} placeholder="Teammate name" className={fieldClass} />
            <select aria-label="Repository member role" value={memberForm.role} onChange={(e) => setMemberForm((s: any) => ({ ...s, role: e.target.value }))} className={fieldClass}>
              <option value="reader">Reader</option>
              <option value="writer">Writer</option>
              <option value="maintainer">Maintainer</option>
              <option value="owner">Owner</option>
            </select>
            <button onClick={() => void onSaveRepoMember()} disabled={busy === 'save-repo-member'} className={buttonSecondary}>{busy === 'save-repo-member' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save member</button>
          </div>
          <div className="mt-4 space-y-3">
            {repoMembers.length === 0 ? <p className="text-sm text-slate-500">No repository members are configured yet.</p> : repoMembers.map((member: WorkspaceRepoMember) => (
              <div key={member.email} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-slate-900">{member.display_name || member.email}</div>
                  <div className="mt-1 text-xs text-slate-500">{member.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs uppercase tracking-wide text-slate-700">{member.role}</span>
                  <button onClick={() => void onDeleteRepoMember(member.email)} disabled={busy === `delete-repo-member-${member.email}`} className={buttonSecondary}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={`${cardClass} p-6`}>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Copy className="h-4 w-4" /> Clone URLs</div>
          <div className="mt-4 grid gap-3">
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">HTTPS</div>
              <div className="flex gap-2">
                <input readOnly aria-label="HTTPS clone URL" data-testid="https-clone-url" value={repoDetail.http_clone_url} className={`${fieldClass} font-mono`} />
                <button onClick={() => void copyText(repoDetail.http_clone_url)} className={buttonSecondary}>Copy</button>
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">SSH</div>
              <div className="flex gap-2">
                <input readOnly aria-label="SSH clone URL" data-testid="ssh-clone-url" value={repoDetail.ssh_clone_url} className={`${fieldClass} font-mono`} />
                <button onClick={() => void copyText(repoDetail.ssh_clone_url)} className={buttonSecondary}>Copy</button>
              </div>
            </div>
          </div>
        </div>
        <div className={`${cardClass} p-6`}>
          <div className="flex items-center justify-between gap-2"><div className="text-sm font-medium text-slate-900">Saved SSH keys</div><button onClick={() => void copyText(authorizedKeys)} className="text-sm font-medium text-slate-700 underline underline-offset-4">Copy authorized_keys</button></div>
          <div className="mt-4 space-y-3">
            {sshKeys.length === 0 ? <p className="text-sm text-slate-500">No SSH keys saved for this account yet.</p> : sshKeys.map((key: WorkspaceSshKey) => <div key={key.key_id} className="rounded-md border border-slate-200 bg-slate-50 px-4 py-4"><div className="flex items-center justify-between gap-4"><div><div className="font-medium text-slate-900">{key.title}</div><div className="mt-1 text-xs text-slate-500">{key.fingerprint}</div></div><button onClick={() => void onDeleteSshKey(key.key_id)} disabled={busy === `ssh-delete-${key.key_id}`} className={buttonSecondary}>Remove</button></div></div>)}
          </div>
          <pre className="mt-5 max-h-48 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-4 text-xs leading-6 text-slate-900">{authorizedKeys || '# No SSH keys saved yet'}</pre>
        </div>
      </section>
    </div>
  )
}

function RepoSettingsTab({ repoDetail, repoSettings, repoMembers, form, setForm, busy, onSave }: any) {
  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_0.88fr]">
      <section className={`${cardClass} p-6`}>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Settings2 className="h-4 w-4" /> Repository settings</div>
        <p className="mt-2 text-sm leading-6 text-slate-600">These settings affect only this repository. They stay separate from account settings and organisation policy.</p>
        <div className="mt-5 grid gap-3">
          <textarea aria-label="Repository description" value={form.description} onChange={(e) => setForm((s: any) => ({ ...s, description: e.target.value }))} placeholder="Describe this repository" className={textareaClass} />
          <div className="grid gap-3 md:grid-cols-2">
            <select aria-label="Repository visibility" value={form.visibility} onChange={(e) => setForm((s: any) => ({ ...s, visibility: e.target.value }))} className={fieldClass}><option value="private">Private</option><option value="internal">Internal</option><option value="public">Public</option></select>
            <select aria-label="Repository default branch" value={form.default_branch} onChange={(e) => setForm((s: any) => ({ ...s, default_branch: e.target.value }))} className={fieldClass}>{repoDetail.branches.map((branch: string) => <option key={branch} value={branch}>{branch}</option>)}</select>
          </div>
          <input aria-label="Repository homepage url" value={form.homepage_url} onChange={(e) => setForm((s: any) => ({ ...s, homepage_url: e.target.value }))} placeholder="https://docs.example.com/repo" className={fieldClass} />
          <input aria-label="Repository documentation path" value={form.documentation_path} onChange={(e) => setForm((s: any) => ({ ...s, documentation_path: e.target.value }))} placeholder="README.md" className={fieldClass} />
          <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={form.branch_protection_enabled} onChange={(e) => setForm((s: any) => ({ ...s, branch_protection_enabled: e.target.checked }))} /> Branch protection enabled</label>
          <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={form.require_pull_request_reviews} onChange={(e) => setForm((s: any) => ({ ...s, require_pull_request_reviews: e.target.checked }))} /> Require pull request reviews before merge</label>
          <button onClick={() => void onSave()} disabled={busy === 'save-repo-settings'} className={buttonPrimary}>{busy === 'save-repo-settings' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings2 className="h-4 w-4" />} Save repository settings</button>
        </div>
      </section>
      <section className="space-y-6">
        <ScopeSettingsSummary title="Repository" icon={FolderGit2} description="Applies only to this repository and the people collaborating in it." policy={repoSettings?.policy} />
        <div className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">Repository policy summary</div>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <InfoRow label="Owners" value={String(repoMembers.filter((member: WorkspaceRepoMember) => member.role === 'owner').length)} />
            <InfoRow label="Maintainers" value={String(repoMembers.filter((member: WorkspaceRepoMember) => member.role === 'maintainer').length)} />
            <InfoRow label="Writers" value={String(repoMembers.filter((member: WorkspaceRepoMember) => member.role === 'writer').length)} />
            <InfoRow label="Readers" value={String(repoMembers.filter((member: WorkspaceRepoMember) => member.role === 'reader').length)} />
          </div>
        </div>
        <LinkCard to="/settings/personal" title="Personal settings" body="SSH keys, clone preference, and your landing defaults live there." />
        <LinkCard to="/settings/organization" title="Organization settings" body="Repository defaults, signup policy, and secure sharing controls live there." />
      </section>
    </div>
  )
}

function PersonalSettingsPage({ settings, form, setForm, sshKeys, busy, onSave }: any) {
  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_0.88fr]">
      <section className={`${cardClass} p-6`}>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><UserCircle2 className="h-4 w-4" /> Personal settings</div>
        <p className="mt-2 text-sm leading-6 text-slate-600">These settings belong to you. Forge reads your identity from Keycloak and stores only your own repository preferences here.</p>
        <div className="mt-5 grid gap-3">
          <MetricBlock label="User" value={settings?.display_name || 'Unknown'} multiline />
          <MetricBlock label="Email" value={settings?.email || 'Unknown'} multiline />
          <MetricBlock label="Roles" value={(settings?.roles || []).join(', ') || 'viewer'} multiline />
          <select aria-label="Preferred clone protocol" value={form.preferred_clone_protocol} onChange={(e) => setForm((s: any) => ({ ...s, preferred_clone_protocol: e.target.value }))} className={fieldClass}><option value="https">Prefer HTTPS</option><option value="ssh">Prefer SSH</option></select>
          <select aria-label="Default landing page" value={form.default_landing_page} onChange={(e) => setForm((s: any) => ({ ...s, default_landing_page: e.target.value }))} className={fieldClass}><option value="repositories">Repositories</option><option value="pulls">Pull requests</option><option value="code">Files</option></select>
          <button onClick={() => void onSave()} disabled={busy === 'save-personal-settings'} className={buttonPrimary}>{busy === 'save-personal-settings' ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCircle2 className="h-4 w-4" />} Save personal settings</button>
        </div>
      </section>
      <section className="space-y-6">
        <ScopeSettingsSummary title="Personal" icon={UserCircle2} description="Applies only to you." policy={settings?.policy} />
        <div className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">SSH identity</div>
          <div className="mt-3 text-sm text-slate-600">{settings?.ssh_key_count || 0} keys currently attached to your Forge account.</div>
          <div className="mt-4 space-y-2">{sshKeys.slice(0, 3).map((key: WorkspaceSshKey) => <div key={key.key_id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{key.title}</div>)}</div>
        </div>
      </section>
    </div>
  )
}

function OrganizationSettingsPage({
  settings,
  form,
  setForm,
  accountContext,
  accountOrgForm,
  setAccountOrgForm,
  accountTeamForm,
  setAccountTeamForm,
  accountInviteForm,
  setAccountInviteForm,
  busy,
  onSave,
  onCreateOrganization,
  onCreateTeam,
  onCreateInvitation,
}: any) {
  const organizations = accountContext?.organizations || []
  const teams = accountContext?.teams || []
  const invitations = accountContext?.invitations || []
  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_0.88fr]">
      <section className={`${cardClass} p-6`}>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Building2 className="h-4 w-4" /> Organization settings</div>
        <p className="mt-2 text-sm leading-6 text-slate-600">These settings shape the current organisation or team workspace. Enterprise tenant boundaries remain separately provisioned by Contextful admins.</p>
        <div className="mt-5 grid gap-3">
          <input aria-label="Organization display name" value={form.org_display_name} onChange={(e) => setForm((s: any) => ({ ...s, org_display_name: e.target.value }))} placeholder="Organization display name" className={fieldClass} />
          <select aria-label="Default repository visibility" value={form.default_repo_visibility} onChange={(e) => setForm((s: any) => ({ ...s, default_repo_visibility: e.target.value }))} className={fieldClass}><option value="private">Private</option><option value="internal">Internal</option><option value="public">Public</option></select>
          <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={form.allow_signup} onChange={(e) => setForm((s: any) => ({ ...s, allow_signup: e.target.checked }))} /> Allow account signup for this installation</label>
          <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={form.require_pull_request_reviews} onChange={(e) => setForm((s: any) => ({ ...s, require_pull_request_reviews: e.target.checked }))} /> Require pull request reviews by default</label>
          <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={form.allow_public_repositories} onChange={(e) => setForm((s: any) => ({ ...s, allow_public_repositories: e.target.checked }))} /> Allow public repositories</label>
          <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={form.allow_remote_imports} onChange={(e) => setForm((s: any) => ({ ...s, allow_remote_imports: e.target.checked }))} /> Allow remote imports</label>
          <select aria-label="Import retention mode" value={form.import_retention_mode} onChange={(e) => setForm((s: any) => ({ ...s, import_retention_mode: e.target.value }))} className={fieldClass}><option value="reference-only">Reference only</option><option value="tenant-mirror">Tenant mirror</option></select>
          <input aria-label="Blocked import hosts" value={form.blocked_import_hosts} onChange={(e) => setForm((s: any) => ({ ...s, blocked_import_hosts: e.target.value }))} placeholder="blocked.example, legacy.internal" className={fieldClass} />
          <button onClick={() => void onSave()} disabled={busy === 'save-org-settings'} className={buttonPrimary}>{busy === 'save-org-settings' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />} Save organization settings</button>
        </div>
      </section>
      <section className="space-y-6">
        <div className={`${cardClass} p-5`}>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><UserCircle2 className="h-4 w-4" /> Account access</div>
          <p className="mt-2 text-sm leading-6 text-slate-600">Admins can shape SaaS access here without creating a new enterprise tenant: organisations contain teams, teams receive invitations, and repositories stay private unless deliberately published.</p>
          <div className="mt-4 grid gap-3">
            <input aria-label="New organization name" value={accountOrgForm.name} onChange={(e) => setAccountOrgForm((s: any) => ({ ...s, name: e.target.value }))} placeholder="New organization" className={fieldClass} />
            <input aria-label="New organization description" value={accountOrgForm.description} onChange={(e) => setAccountOrgForm((s: any) => ({ ...s, description: e.target.value }))} placeholder="Organization description" className={fieldClass} />
            <button onClick={() => void onCreateOrganization()} disabled={busy === 'create-account-organization'} className={buttonSecondary}>{busy === 'create-account-organization' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add organization</button>
          </div>
          <div className="mt-5 grid gap-3">
            <select aria-label="Team organization" value={accountTeamForm.organization_id} onChange={(e) => setAccountTeamForm((s: any) => ({ ...s, organization_id: e.target.value }))} className={fieldClass}>
              {organizations.map((org: any) => <option key={org.organization_id} value={org.organization_id}>{org.name}</option>)}
            </select>
            <input aria-label="New team name" value={accountTeamForm.name} onChange={(e) => setAccountTeamForm((s: any) => ({ ...s, name: e.target.value }))} placeholder="New team" className={fieldClass} />
            <input aria-label="New team description" value={accountTeamForm.description} onChange={(e) => setAccountTeamForm((s: any) => ({ ...s, description: e.target.value }))} placeholder="Team description" className={fieldClass} />
            <button onClick={() => void onCreateTeam()} disabled={busy === 'create-account-team'} className={buttonSecondary}>{busy === 'create-account-team' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add team</button>
          </div>
          <div className="mt-5 grid gap-3">
            <input aria-label="Invitation email" value={accountInviteForm.email} onChange={(e) => setAccountInviteForm((s: any) => ({ ...s, email: e.target.value }))} placeholder="person@example.com" className={fieldClass} />
            <select aria-label="Invitation role" value={accountInviteForm.role} onChange={(e) => setAccountInviteForm((s: any) => ({ ...s, role: e.target.value }))} className={fieldClass}>
              <option value="member">Member</option>
              <option value="reader">Reader</option>
              <option value="team-admin">Team admin</option>
              <option value="organization-admin">Organization admin</option>
              <option value="owner">Owner</option>
            </select>
            <select aria-label="Invitation organization" value={accountInviteForm.organization_id} onChange={(e) => setAccountInviteForm((s: any) => ({ ...s, organization_id: e.target.value }))} className={fieldClass}>
              <option value="">No organization</option>
              {organizations.map((org: any) => <option key={org.organization_id} value={org.organization_id}>{org.name}</option>)}
            </select>
            <select aria-label="Invitation team" value={accountInviteForm.team_id} onChange={(e) => setAccountInviteForm((s: any) => ({ ...s, team_id: e.target.value }))} className={fieldClass}>
              <option value="">No team</option>
              {teams.map((team: any) => <option key={team.team_id} value={team.team_id}>{team.name}</option>)}
            </select>
            <button onClick={() => void onCreateInvitation()} disabled={busy === 'create-account-invitation'} className={buttonPrimary}>{busy === 'create-account-invitation' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Create invitation</button>
          </div>
        </div>
        <div className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">Organizations and teams</div>
          <div className="mt-4 space-y-3">
            {organizations.map((org: any) => (
              <div key={org.organization_id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                <div className="font-medium text-slate-900">{org.name}</div>
                <div className="mt-1 text-slate-600">{org.description || `${org.team_count} teams`}</div>
              </div>
            ))}
            {teams.map((team: any) => (
              <div key={team.team_id} className="rounded-md border border-slate-200 bg-white px-3 py-3 text-sm">
                <div className="font-medium text-slate-900">{team.name}</div>
                <div className="mt-1 text-slate-600">{team.description || 'Team access group'}</div>
              </div>
            ))}
          </div>
        </div>
        <div className={`${cardClass} p-5`}>
          <div className="text-sm font-medium text-slate-900">Pending invitations</div>
          <div className="mt-4 space-y-3">
            {invitations.length === 0 ? <p className="text-sm text-slate-500">No pending invitations.</p> : invitations.map((invitation: WorkspaceAccountInvitation) => (
              <div key={invitation.invitation_id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                <div>
                  <div className="font-medium text-slate-900">{invitation.email}</div>
                  <div className="mt-1 text-slate-600">{invitation.role}</div>
                </div>
                <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-600">{invitation.status}</span>
              </div>
            ))}
          </div>
        </div>
        <ScopeSettingsSummary title="Organization" icon={Building2} description="Applies to repositories in this organization or team." policy={settings?.policy} />
        <ScopeSettingsSummary title="Account" icon={UserCircle2} description="Applies to SaaS account organizations, teams, and invitations." policy={accountContext?.policy} />
        <LinkCard to="/settings/personal" title="Personal settings" body="Your account preferences stay separate from organization policy." />
      </section>
    </div>
  )
}

function ScopeSettingsSummary({ title, icon: Icon, description, policy }: { title: string; icon: any; description: string; policy?: { managed_by: string; required_roles: string[]; keycloak_source: string; isolation: string } }) {
  return (
    <div className={`${cardClass} p-5`}>
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Icon className="h-4 w-4" /> {title}</div>
      <div className="mt-3 text-sm text-slate-600">{description}</div>
      {policy ? (
        <div className="mt-4 space-y-3 text-sm text-slate-600">
          <InfoRow label="Managed by" value={policy.managed_by} />
          <InfoRow label="Required roles" value={policy.required_roles.length ? policy.required_roles.join(', ') : 'self-service'} />
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3"><div className="font-medium text-slate-900">Keycloak and policy boundary</div><div className="mt-1">{policy.keycloak_source}</div></div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3"><div className="font-medium text-slate-900">Isolation</div><div className="mt-1">{policy.isolation}</div></div>
        </div>
      ) : null}
    </div>
  )
}

function ScopeCard({ icon: Icon, title, body }: { icon: any; title: string; body: string }) {
  return <div className={`${subtleCardClass} p-4`}><div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Icon className="h-4 w-4" /> {title}</div><p className="mt-2 text-sm leading-6 text-slate-600">{body}</p></div>
}

function LinkCard({ to, title, body }: { to: string; title: string; body: string }) {
  return <Link to={to} className={`${cardClass} block p-5 transition hover:border-slate-300 hover:bg-slate-50`}><div className="flex items-center gap-2 text-sm font-medium text-slate-900"><ChevronRight className="h-4 w-4" /> {title}</div><p className="mt-2 text-sm leading-6 text-slate-600">{body}</p></Link>
}

function MetricBlock({ label, value, multiline = false, dark = false }: { label: string; value: string; multiline?: boolean; dark?: boolean }) {
  return <div className={`rounded-md border px-3 py-3 ${dark ? 'border-slate-800 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'}`}><div className={`text-xs uppercase tracking-wide ${dark ? 'text-slate-400' : 'text-slate-400'}`}>{label}</div><div className={`mt-1 font-medium ${multiline ? 'break-all text-sm' : 'text-lg'}`}>{value}</div></div>
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between gap-3 text-sm"><span className="text-slate-500">{label}</span><span className="text-right font-medium text-slate-900">{value}</span></div>
}

function RepoListDatum({ label, value }: { label: string; value: string }) {
  return <div><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div><div className="mt-1 text-sm font-medium text-slate-900">{value}</div></div>
}
